import { useNewItems } from '../../../lib/sectionNewItems'
import { NewItemDot } from '../NewItemDot'
import { useEffect, useRef, useState } from 'react'
import { can, type ProjectAccessRole } from '@webbuilder/shared-types'
import {
  IconArrowNarrowRight,
  IconArrowBackUp as IconEventRefund,
  IconChevronRight as IconEventGo,
  IconUserPlus as IconEventJoined,
  IconCheck as IconNoteCheck,
  IconPencil as IconNotePencil,
  IconSend as IconNoteSend,
  IconTrash as IconNoteTrash,
  IconX as IconNoteX,
} from '@tabler/icons-react'
import { useTabThumb } from '../../../hooks/useTabThumb'
import { COMMAND_KEY, NoteAvatar, relativeNoteTime } from '../../../lib/noteFeed'
import {
  NOTE_TEXT_MAX,
  mergeNotes,
  notesFit,
  parseNotes,
  serializeNotes,
  type ProjectNoteItem,
} from '../../../lib/projectNotes'
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { PickerField, type KnownOption } from './BlogPickerField'
import { createContact, deleteContact, getBooking, getContact, listContactTags, listContacts, removeContactTag, updateContact, type BillingPlan, type Booking, type ContactPatch, type ContactBooking, type ContactBookingChange, type ContactPurchase, type ContactRecord, type ContactStatus, type FormSubmission } from '../../../lib/api'
import { fetchTeam, getOrder, setSubmissionStatus, type OrderDetail } from '../../../lib/api'
import { downloadCsv } from '../../../lib/csv'
import { BookingDetail } from './BookingDetail'
import { OrderDetailDialog } from './OrderDetailDialog'
import { StarFilterButton } from '../StarFilterButton'
import { ConfirmDialog } from '../ConfirmDialog'
import { ContactAccountCard } from './ContactAccountCard'
import { ContactAccessCard } from './ContactAccessCard'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { useToast } from '../../Toast'
import { useI18n } from '../../../lib/i18n'
import { Pagination, useDebounced, useServerPaged } from '../Pagination'
import { PlanUsageSummary } from '../PlanUsageSummary'
import { SITE_PLAN_LIMITS } from '../../../lib/planLimits'
import { markDashboardItemsSeen } from '../../../lib/dashboardUnread'
import { formatDashboardListDate, formatDashboardListDateWithTime, formatDashboardListDay } from '../../../lib/dashboardListDate'
import { readStarredIds, setStarred as setStarredIds } from '../../../lib/dashboardStars'
import { HeaderRefreshButton, ListToolbar, SelectCheckbox, StarButton, useListSelection } from '../ListSelection'
import { BOOKING_STATUS_TONE } from './bookingShared'
import { BookingOverdueMark } from './BookingOverdueMark'
import { formatMoney } from './offerShared'
import { IconActivity, IconAddressBook, IconAdjustmentsHorizontal, IconArrowLeft, IconBan, IconCalendarEvent, IconCalendarTime, IconCheck, IconChevronDown, IconClipboardList, IconCopy, IconDots, IconDownload, IconEye, IconGift, IconMail, IconMapPin, IconNote, IconPencil, IconPhone, IconSearch, IconShieldOff, IconStar, IconSettings, IconShoppingBag, IconTag, IconTrash, IconUser, IconUserCheck, IconUserPlus, IconX } from '@tabler/icons-react'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { openOnRowClick } from '../rowOpen'
import { usePhoneViewport } from '../../../hooks/usePhoneViewport'

/** Cuántas tarjetas de actividad se enseñan de golpe en el teléfono. */
const ACTIVITY_PAGE = 10

/** Filas por página. */
const PAGE_SIZE = 10
/* Con el filtro de destacados puesto se pide una página grande y se recorta
   aquí: las estrellas viven en este navegador —no en el servidor—, así que él no
   puede filtrar por ellas. Es el mismo trato que en Formularios. */
const SERVER_MAX_PAGE_SIZE = 100

/** La ficha abierta trae además su historial; la lista no, para no engordarla. */
type ContactDetail = ContactRecord & {
  activities: FormSubmission[]
  purchases: ContactPurchase[]
  bookings: ContactBooking[]
  bookingChanges: ContactBookingChange[]
}

type ProfileTab = 'activity' | 'forms' | 'purchases' | 'bookings' | 'memberships' | 'notes'

type ContactSort = 'nameAsc' | 'nameDesc' | 'emailAsc' | 'emailDesc' | 'sourceAsc' | 'sourceDesc' | 'firstAsc' | 'firstDesc' | 'lastAsc' | 'lastDesc'


function fieldValue(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}


/**
 * Identidad de un contacto a partir de un envío.
 *
 * Una sola regla, exportada, porque cualquier otra pantalla que quiera enlazar
 * a una ficha tiene que llegar al mismo id: si se copia y luego diverge, el
 * enlace deja de encontrar al contacto y nadie se entera.
 */
export function contactIdForSubmission(submission: { id: string; email: string }): string {
  const email = submission.email.trim()
  // El nombre no sirve como identidad: dos anónimos pueden compartirlo.
  return email ? `email:${email.toLocaleLowerCase()}` : `submission:${submission.id}`
}

/**
 * La lista de personas ya no se arma aquí.
 *
 * Agrupar los envíos por correo en el navegador obligaba a descargarlos todos,
 * y con el tope que tenía la bandeja quien sólo aparecía en un envío antiguo no
 * llegaba a existir. Ahora esa misma agrupación la hace la consulta, que además
 * puede filtrar, ordenar y paginar antes de enviar nada.
 */

function fieldLabel(key: string, fallback: string): string {
  const label = key.replace(/^_+/, '').replace(/[_-]+/g, ' ').trim()
  return label ? label.charAt(0).toLocaleUpperCase() + label.slice(1) : fallback
}

function answers(submission: FormSubmission, fallback: string): Array<[string, string]> {
  return Object.entries(submission.data)
    .filter(([key, value]) => !key.startsWith('_wb_') && fieldValue(value))
    .map(([key, value]) => [fieldLabel(key, fallback), fieldValue(value)])
}

/** Lo que este cliente ha dejado. Sale de las compras, que aún no existen. */
/**
 * Lo que ha pagado en total.
 *
 * En la moneda en que se cobró, que la trae cada cobro: el proyecto puede
 * cobrar en dos monedas y sumarlas en una sola cifra sería inventarse un tipo
 * de cambio. Con varias, se enseña cada una por separado.
 */
function purchaseTotal(purchases: ContactPurchase[]): string {
  if (purchases.length === 0) return formatMoney(0, 'mxn')
  const byCurrency = new Map<string, number>()
  for (const purchase of purchases) {
    byCurrency.set(purchase.currency, (byCurrency.get(purchase.currency) ?? 0) + purchase.amountTotal)
  }
  return [...byCurrency].map(([currency, amount]) => formatMoney(amount, currency)).join(' · ')
}

function initials(contact: ContactRecord): string {
  const source = contact.name || contact.email || '?'
  return source.split(/\s+/).slice(0, 2).map((part) => part.charAt(0)).join('').toLocaleUpperCase()
}

/** La URL completa sirve al copiar; en una tarjeta basta dominio + ruta. */
function compactPageUrl(value: string | null | undefined): string {
  if (!value) return ''
  try {
    const parsed = new URL(value)
    return `${parsed.hostname}${parsed.pathname === '/' ? '' : parsed.pathname}`
  } catch {
    return value
  }
}

/** Una referencia reconocible sin convertir la tarjeta en una fila técnica. */
function shortReference(id: string): string {
  return id.length > 8 ? id.slice(-8).toLocaleUpperCase() : id.toLocaleUpperCase()
}

function formatContactPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7)}`
  }
  return phone
}

/** Portada real cuando existe; si no, una miniatura editorial con el icono. */
/**
 * Cuándo es la cita, entera: día, de qué hora a qué hora y en qué zona.
 *
 * Vive aquí y no en cada pantalla porque Actividad y la pestaña de Reservas
 * enseñan la misma cita: la tenían escrita dos veces y habían divergido —la
 * pestaña decía sólo la hora de inicio, sin el final ni la zona—, así que la
 * misma reserva contaba dos cosas distintas según dónde se mirara.
 *
 * Las horas se calculan en la zona CONGELADA de la reserva, no en la de quien
 * mira: es la hora a la que el negocio tiene que estar, y traducirla al huso de
 * quien abre el panel desde otro país diría una hora a la que no pasa nada. Pero
 * el nombre de la zona no se escribe: «America/Los_Angeles» es una etiqueta
 * técnica que alarga la línea y no le dice nada a quien la lee, que ya está
 * mirando las horas de su propio negocio. Donde sí hace falta —la ficha de la
 * cita— se enseña con su nombre humano y en su propia casilla.
 */
function bookingWhen(booking: { startsAt: string; endsAt: string; timezone: string }, language: string): string {
  const day = new Intl.DateTimeFormat(language, {
    timeZone: booking.timezone, weekday: 'short', day: 'numeric', month: 'short',
  }).format(new Date(booking.startsAt))
  const clock = (iso: string) => new Intl.DateTimeFormat(language, {
    timeZone: booking.timezone, hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso))
  return `${day} · ${clock(booking.startsAt)}–${clock(booking.endsAt)}`
}

/**
 * La marca de cada suceso de la cronología: siempre el icono de lo que es.
 *
 * Llevaba la portada de la oferta cuando la había, así que una misma lista
 * mezclaba fotos con iconos: tres filas con la misma foto del café no decían
 * cuál era la compra y cuál la cita —la foto es del servicio, no del suceso—, y
 * al lado de las que no tenían portada parecían dos listas distintas. Con el
 * icono siempre, la columna dice de un vistazo QUÉ pasó, que es para lo que se
 * mira una cronología. Qué se compró lo dice el título, al lado.
 */
function ContactCardThumb({ icon: Icon, tone }: {
  icon: typeof IconActivity
  tone: 'form' | 'purchase' | 'booking' | 'note' | 'contact'
}) {
  return (
    <span className={`dash-cp-item-thumb is-${tone} dash-widget-glass-icon is-glass`} aria-hidden="true">
      <Icon size={17} />
    </span>
  )
}

/**
 * Las etiquetas de la ficha: el mismo campo que las categorías del blog
 * (`PickerField`). Se busca entre las que ya llevan otros contactos —con
 * cuántos las llevan— o se crea escribiéndola, y desde la lista se puede
 * eliminar una de TODOS los contactos; para quitarla sólo de éste está la ✕ de
 * su pastilla. Sin catálogo fijo: el vocabulario de cada negocio es suyo.
 */
const CONTACT_TAG_TEXTS = {
  label: 'contacts.tags',
  placeholder: 'contacts.tags.placeholder',
  create: 'contacts.tags.create',
  empty: 'contacts.tags.empty',
  allChosen: 'contacts.tags.allChosen',
  limit: 'contacts.tags.limit',
  remove: 'contacts.tags.removeOne',
  deleteAria: 'contacts.tags.deleteAria',
  deleteTitle: 'contacts.tags.deleteTitle',
  deleteBody: 'contacts.tags.deleteBody',
  deleteBodyOne: 'contacts.tags.deleteBodyOne',
  deleteConfirm: 'contacts.tags.deleteConfirm',
  deleted: 'contacts.tags.deleted',
  deletedOne: 'contacts.tags.deletedOne',
  count: 'contacts.tags.count',
  countOne: 'contacts.tags.countOne',
}

/**
 * «Cuenta»: la persona entró alguna vez a su cuenta del sitio. Una pastilla
 * junto al nombre y no una columna, como Shopify: casi todos los contactos no
 * la tienen y una columna entera de «Sin cuenta» sólo haría ruido.
 */
function AccountBadge({ label, hint, iconOnly = false }: { label: string; hint: string; iconOnly?: boolean }) {
  return (
    <span className={`dash-contact-account-badge${iconOnly ? ' is-icon-only' : ''}`} data-tooltip={hint} title={hint} aria-label={hint} {...(iconOnly ? { role: 'img' } : {})}>
      <IconUserCheck size={iconOnly ? 14 : 11} aria-hidden="true" />
      {iconOnly ? null : label}
    </span>
  )
}

export function ContactsSection({ projectId, token, plan, onOpenPlans, projectRole = 'owner', currentUser, onOpenOffer, initialContactId, onConsumeInitialContact, onRecordChange }: {
  projectId: string
  token: string
  plan: BillingPlan
  onOpenPlans: () => void
  /** El dueño y los administradores pueden moderar las notas de todo el equipo. */
  projectRole?: ProjectAccessRole | undefined
  /** Quién firma los comentarios de la ficha. El mismo que firma los del equipo. */
  currentUser?: { id: string; name: string; avatarUrl?: string | null } | undefined
  /** Abre la oferta que se vendió, desde la ficha de un cobro de esta persona. */
  onOpenOffer?: ((offerId: string) => void) | undefined
  /** Ficha a abrir al entrar, cuando se llega desde otra sección. */
  initialContactId?: string | null | undefined
  onConsumeInitialContact?: (() => void) | undefined
  /** Refleja en la URL la ficha abierta, para que "atrás" vuelva a la lista. */
  onRecordChange?: ((record: string | null) => void) | undefined
}) {
  const { showToast } = useToast()
  const { t, fmt, language } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('contacts.searchPlaceholder')
  const profileTabs: Array<{ id: ProfileTab; label: string; icon: typeof IconActivity }> = [
    { id: 'activity', label: t('contacts.tab.activity'), icon: IconActivity },
    { id: 'forms', label: t('contacts.tab.forms'), icon: IconClipboardList },
    { id: 'purchases', label: t('contacts.tab.purchases'), icon: IconShoppingBag },
    /* Junto a Compras y no dentro: una cita y una venta no son lo mismo aunque
       la de pago deje un cobro detrás —la mitad de las citas se pagan en el
       local y no dejan ninguno—, y lo que se busca aquí es cuándo vino, no
       cuánto pagó. */
    { id: 'bookings', label: t('contacts.tab.bookings'), icon: IconCalendarEvent },
    /* Lo que tiene: membresías de pago y gratis, paquetes y accesos dados. */
    { id: 'memberships', label: t('contacts.tab.memberships'), icon: IconStar },
    { id: 'notes', label: t('contacts.tab.notes'), icon: IconNote },
  ]
  /** Cómo se llama cada origen. En un solo sitio: lo dicen la lista, la ficha y
      el filtro, y con un ternario repetido en los tres uno se queda atrás. */
  const sourceLabel = (source: ContactRecord['initialSource']) => t(`contacts.source.${source}`)
  const answerCountLabel = (count: number) => t(
    count === 1 ? 'contacts.card.answerOne' : 'contacts.card.answersMany',
    { count },
  )
  const purchaseStatusLabel = (status: string) => {
    const normalized = status.toLocaleLowerCase()
    if (['paid', 'succeeded', 'complete', 'completed'].includes(normalized)) return t('contacts.purchase.status.paid')
    if (normalized === 'refunded') return t('contacts.purchase.status.refunded')
    if (['pending', 'processing'].includes(normalized)) return t('contacts.purchase.status.pending')
    if (normalized === 'failed') return t('contacts.purchase.status.failed')
    if (['canceled', 'cancelled'].includes(normalized)) return t('contacts.purchase.status.cancelled')
    return fieldLabel(status, t('contacts.purchase.status.unknown'))
  }
  const bookingDurationLabel = (booking: ContactBooking) => {
    const minutes = Math.max(0, Math.round((new Date(booking.endsAt).getTime() - new Date(booking.startsAt).getTime()) / 60_000))
    if (minutes >= 60 && minutes % 60 === 0) {
      const hours = minutes / 60
      return t(hours === 1 ? 'contacts.booking.durationHourOne' : 'contacts.booking.durationHoursMany', { count: hours })
    }
    return t(minutes === 1 ? 'contacts.booking.durationMinuteOne' : 'contacts.booking.durationMinutesMany', { count: minutes })
  }
  const [contacts, setContacts] = useState<ContactRecord[]>([])
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState<ContactDetail | null>(null)
  const [detailFailed, setDetailFailed] = useState(false)
  /** Se incrementa tras editar o borrar, para volver a pedir lista y ficha. */
  const [reloadKey, setReloadKey] = useState(0)
  /* Las etiquetas que ya se usan en el proyecto, para elegir en vez de
     reescribirlas. Se piden aparte —la lista va por páginas— y otra vez tras
     cada cambio, que puede haber creado o vaciado una. */
  const [knownTags, setKnownTags] = useState<KnownOption[]>([])
  useEffect(() => {
    let alive = true
    listContactTags(projectId, token).then((tags) => { if (alive) setKnownTags(tags) }).catch(() => {})
    return () => { alive = false }
  }, [projectId, token, reloadKey])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const isNewContact = useNewItems('contacts', contacts, !loading && !failed)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<ContactSort>('lastDesc')
  const [sourceFilter, setSourceFilter] = useState<'all' | ContactRecord['initialSource']>('all')
  /** Por defecto sólo activos: spam y bloqueados existen, pero no estorban. */
  const [statusFilter, setStatusFilter] = useState<'all' | ContactStatus>('active')
  /* La cuenta del sitio, como en Shopify: un filtro más sobre las mismas personas. */
  const [accountFilter, setAccountFilter] = useState<'all' | 'yes' | 'no'>('all')
  const [filterMenuOpen, setFilterMenuOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(initialContactId ?? '')
  const [profileTab, setProfileTab] = useState<ProfileTab>('activity')
  /* En el teléfono la actividad se enseña de diez en diez: con muchas tarjetas
     la pestaña se volvía una página interminable, y «Mostrar más» al pie añade
     otras diez. En pantalla ancha se ven todas, como siempre. */
  const isPhone = usePhoneViewport()
  const [activityShown, setActivityShown] = useState(ACTIVITY_PAGE)
  /* La nota a la que se acaba de saltar desde la cronología. Se apaga sola: es
     un «ésta es», no un estado que haya que deshacer a mano. */
  const [highlightedNote, setHighlightedNote] = useState<string | null>(null)
  /*
   * La pastilla que se desliza, medida sobre la pestaña activa: el mismo gesto
   * que los catálogos y la ficha de una oferta.
   *
   * Con un ref de ESTADO y no de objeto: la barra sólo existe con un contacto
   * abierto, y un ref de objeto no avisa cuando aparece, así que el efecto no
   * volvería a medir y la pastilla se quedaría sin sitio ni ancho. El
   * observador cubre lo demás: una cuenta que aparece cambia el ancho de su
   * pestaña, y con él el de la barra.
   */
  const [tabsEl, setTabsEl] = useState<HTMLDivElement | null>(null)
  useTabThumb(tabsEl, profileTab)

  /** Abrir o cerrar una ficha es navegación, así que pasa por la URL. */
  const openContact = (id: string) => {
    if (id) markDashboardItemsSeen(projectId, 'contacts', [id])
    setSelectedId(id)
    onRecordChange?.(id || null)
  }

  const [contactToDelete, setContactToDelete] = useState<ContactRecord | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  /** Posición del menú en el viewport. Se porta a `body` para que las filas
     vecinas nunca puedan quedar por encima de sus acciones. */
  const [listMenu, setListMenu] = useState<{ top: number; left: number; up: boolean; maxHeight: number }>({ top: 0, left: 0, up: false, maxHeight: 0 })

  useEffect(() => {
    if (!menuOpenId) return
    function onDown(event: PointerEvent) {
      const el = event.target as Element | null
      if (!el?.closest?.('.dash-project-row-menu-wrap') && !el?.closest?.('.dash-contact-row-menu-portal')) setMenuOpenId(null)
    }
    function onKey(event: KeyboardEvent) { if (event.key === 'Escape') setMenuOpenId(null) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [menuOpenId])
  /**
   * Y lo mismo en la cronología, con su propio estado.
   *
   * Las respuestas salían siempre desplegadas, así que un envío de seis campos
   * empujaba hacia abajo todo lo que había pasado antes y la cronología dejaba
   * de leerse como una cronología. Cerrado se ve QUÉ pasó —«Envió Contacto, 3
   * respuestas»— y se abre lo que se quiera mirar, igual que en la pestaña de
   * Formularios. Aparte de `expandedForm` a propósito: son dos listas, y
   * compartir el estado haría que abrir en una moviera la otra.
   */
  const [expandedAnswers, setExpandedAnswers] = useState('')
  /**
   * Abrir un mensaje en la ficha es haberlo leído.
   *
   * La tarjeta sólo dice «Nuevo» mientras nadie lo ha abierto, así que abrirlo
   * aquí tiene que contar igual que abrirlo en la bandeja de Formularios —que ya
   * lo marca al entrar—. Si no, el aviso seguiría encendido sobre un mensaje que
   * se acaba de leer entero, y habría que ir a Formularios a apagarlo.
   *
   * Se pinta leído antes de que conteste el servidor, como en la bandeja: si la
   * marca no llega a guardarse, la próxima vez que se cargue el contacto volverá
   * a salir como nuevo, que es el fallo menos dañino posible.
   */
  const toggleAnswers = (submission: FormSubmission) => {
    const abriendo = expandedAnswers !== submission.id
    setExpandedAnswers(abriendo ? submission.id : '')
    if (!abriendo || submission.status !== 'unread') return
    markDashboardItemsSeen(projectId, 'forms', [submission.id])
    setSelected((contact) => contact ? {
      ...contact,
      activities: contact.activities.map((item) => item.id === submission.id ? { ...item, status: 'read' as const } : item),
    } : contact)
    void setSubmissionStatus(projectId, submission.id, 'read', token).catch(() => {})
  }
  /* «Dar acceso» en la cabecera: la tarjeta Accesos dice si se puede y abre su ventana. */
  const [canGrantAccess, setCanGrantAccess] = useState(false)
  const [grantSignal, setGrantSignal] = useState(0)
  const [editOpen, setEditOpen] = useState(false)
  const [savingField, setSavingField] = useState('')

  /* El cobro se abre aquí mismo, sin salir de la persona.
     Llevaba a Pedidos y allí había que volver a buscar la fila: el contacto es
     el hilo que se está siguiendo, y el cobro es un detalle suyo. Se pide
     entero —la ficha del contacto sólo trae el resumen— y el diálogo es el
     mismo de Pedidos, con su recibo de Stripe. */
  const [orderOpen, setOrderOpen] = useState<OrderDetail | null>(null)
  const [orderLoading, setOrderLoading] = useState('')
  const openOrder = (orderId: string) => {
    /* La tarjeta entera abre el pedido, así que un doble clic no puede lanzar
       dos peticiones: antes lo evitaba el `disabled` del enlace. */
    if (orderLoading === orderId) return
    setOrderLoading(orderId)
    getOrder(projectId, orderId, token)
      .then((order) => setOrderOpen(order))
      .catch((error: unknown) => {
        console.error('[contactos] no se pudo abrir el cobro', error)
        showToast(t('contacts.activity.orderFailed'), 'error')
      })
      .finally(() => setOrderLoading(''))
  }

  /* La tarjeta del contacto sólo trae un resumen de la cita. Al abrirla se
     pide la misma ficha completa que usan Calendario y Servicios, sin sacar a
     nadie del perfil que estaba revisando. */
  const [bookingOpen, setBookingOpen] = useState<Booking | null>(null)
  const [bookingLoading, setBookingLoading] = useState('')
  /*
   * La cita se pide en cuanto el cursor (o el foco) llega a su tarjeta, y se
   * guarda: al pulsar, normalmente ya está y el panel se abre en el acto. Antes
   * se pedía al pulsar y el panel esperaba el viaje entero al servidor. Una
   * petición en curso se reutiliza, no se repite. Se vacía al cambiar de ficha.
   */
  const bookingCache = useRef(new Map<string, Promise<Booking>>())
  useEffect(() => { bookingCache.current.clear() }, [selectedId])
  const fetchBooking = (bookingId: string) => {
    let pending = bookingCache.current.get(bookingId)
    if (!pending) {
      pending = getBooking(projectId, bookingId, token)
      // Si falla, que el próximo intento vuelva a pedirla.
      pending.catch(() => { bookingCache.current.delete(bookingId) })
      bookingCache.current.set(bookingId, pending)
    }
    return pending
  }
  const prefetchBooking = (bookingId: string) => { void fetchBooking(bookingId).catch(() => {}) }
  const openBooking = (bookingId: string) => {
    if (bookingLoading) return
    setBookingLoading(bookingId)
    fetchBooking(bookingId)
      .then((booking) => setBookingOpen(booking))
      .catch((error: unknown) => {
        console.error('[contactos] no se pudo abrir la cita', error)
        showToast(t('contacts.activity.bookingFailed'), 'error')
      })
      .finally(() => setBookingLoading(''))
  }
  const applyContactBooking = (booking: Booking) => {
    // La guardada pasa a ser la de ahora: reabrirla no enseña la de antes.
    bookingCache.current.set(booking.id, Promise.resolve(booking))
    setBookingOpen(booking)
    setSelected((contact) => contact ? {
      ...contact,
      bookings: contact.bookings.map((item) => item.id === booking.id ? {
        ...item,
        startsAt: booking.startsAt,
        endsAt: booking.endsAt,
        status: booking.status,
      } : item),
    } : contact)
    /* El cambio ya quedó escrito en la bitácora del servidor. Volver a pedir la
       ficha hace aparecer su card en Activity sin tener que cerrar o recargar. */
    setReloadKey((key) => key + 1)
  }

  /* ── Los comentarios de la ficha ──────────────────────────────────────────
     El mismo hilo que el widget de notas del equipo y con el mismo almacén: un
     array JSON en la columna `note` del contacto, con quién escribió, cuándo y
     qué. Era un bloc de notas de uno: quien lo abría reescribía encima de lo
     que hubiera puesto otro, sin rastro de quién ni cuándo. */
  const [noteItems, setNoteItems] = useState<ProjectNoteItem[]>([])
  const [noteDraft, setNoteDraft] = useState('')
  const [noteEditingId, setNoteEditingId] = useState<string | null>(null)
  const [noteEditText, setNoteEditText] = useState('')
  const [noteDeletingId, setNoteDeletingId] = useState<string | null>(null)
  const [notesSaving, setNotesSaving] = useState(false)
  const [notesFull, setNotesFull] = useState(false)
  const [notesFailed, setNotesFailed] = useState(false)
  /* Lo borrado en esta sesión: es lo único que distingue «la borré» de «aún no
     la había visto» al fusionar con lo que tenga el servidor. */
  const noteDeletedIds = useRef<Set<string>>(new Set())

  /**
   * Quién escribió cada nota, con su foto de hoy.
   *
   * La nota guarda el nombre y la foto del día en que se escribió, así que una
   * de hace meses enseñaba la inicial de alguien que desde entonces subió su
   * retrato. El equipo del proyecto lo sabe, así que se pregunta una vez y se
   * cruza por id —y por nombre, para las notas viejas que no guardaron id—.
   * Es la misma resolución que el widget de notas del equipo.
   */
  const [teamPeople, setTeamPeople] = useState<Record<string, { name: string; avatarUrl: string | null }>>({})
  useEffect(() => {
    if (!projectId || !token) return
    let alive = true
    fetchTeam(projectId, token)
      .then((snap) => {
        if (!alive) return
        const map: Record<string, { name: string; avatarUrl: string | null }> = {}
        const add = (person: { id?: string | undefined; name?: string | undefined; avatarUrl?: string | null | undefined } | null | undefined) => {
          if (!person) return
          const entry = { name: (person.name ?? '').trim(), avatarUrl: person.avatarUrl ?? null }
          if (person.id) map[person.id] = entry
          if (entry.name) map[`name:${entry.name.toLowerCase()}`] = entry
        }
        add(snap.owner)
        for (const member of snap.members ?? []) add(member)
        setTeamPeople(map)
      })
      .catch(() => {
        /* Sin equipo se sigue con lo que guardó la nota: es un adorno, no un dato. */
      })
    return () => { alive = false }
  }, [projectId, token])

  /** El autor de una nota tal como se debe enseñar hoy: nombre y foto. */
  const noteAuthor = (note: ProjectNoteItem): { name: string; avatarUrl: string | null } => {
    const isMe = Boolean(currentUser?.id && note.userId === currentUser.id)
    if (isMe) return { name: currentUser?.name ?? note.userName, avatarUrl: currentUser?.avatarUrl ?? note.userAvatar ?? null }
    const fromTeam = (note.userId ? teamPeople[note.userId] : undefined)
      ?? (note.userName ? teamPeople[`name:${note.userName.trim().toLowerCase()}`] : undefined)
    return {
      name: fromTeam?.name || note.userName,
      avatarUrl: fromTeam?.avatarUrl ?? note.userAvatar ?? null,
    }
  }

  /**
   * Quién movió una cita, con su cara.
   *
   * Mismo criterio que en las notas: manda el id —de ahí salen la foto y el
   * nombre de HOY— y el nombre copiado en su día queda de respaldo para quien ya
   * no está en el equipo. Antes se enseñaba lo copiado a secas, que hasta ahora
   * era el correo: «fulano@ejemplo.com movió la cita» identifica una cuenta, no
   * nombra a una persona.
   */
  const changeActor = (change: ContactBookingChange): { name: string; avatarUrl: string | null } => {
    if (currentUser?.id && change.actorUserId === currentUser.id) {
      return { name: currentUser.name ?? change.actorName, avatarUrl: currentUser.avatarUrl ?? null }
    }
    const fromTeam = change.actorUserId ? teamPeople[change.actorUserId] : undefined
    return { name: fromTeam?.name || change.actorName, avatarUrl: fromTeam?.avatarUrl ?? null }
  }

  /** Cada miembro administra lo suyo; admin y fundador moderan todo el hilo. */
  const canManageNote = (note: ProjectNoteItem): boolean => {
    const isMe = Boolean(currentUser?.id && note.userId === currentUser.id)
    return can(projectRole, 'contacts.write') && (isMe || !note.userId || can(projectRole, 'team.manage'))
  }

  /**
   * Toda edición pasa por aquí: parchea en el servidor y vuelve a pedir.
   *
   * Antes se fusionaba la respuesta en la lista que había en memoria, algo que
   * sólo funcionaba porque la lista estaba entera. Con la lista paginada, quien
   * decide qué persona sale y en qué orden es la consulta, así que reconstruirla
   * a mano acabaría enseñando algo distinto de lo que hay guardado.
   */
  const patchContact = async (contact: ContactRecord, patch: ContactPatch, field: string) => {
    setSavingField(field)
    try {
      await updateContact(projectId, contact.id, patch, token)
      setReloadKey((key) => key + 1)
    } finally {
      setSavingField('')
    }
  }

  const removeContact = async (contact: ContactRecord) => {
    await deleteContact(projectId, contact.id, token)
    setContactToDelete(null)
    openContact('')
    setReloadKey((key) => key + 1)
  }

  // Cada contacto se abre en Actividad: heredar la pestaña del anterior te
  // dejaba mirando unas notas vacías sin saber por qué.
  useEffect(() => { setProfileTab('activity'); setActivityShown(ACTIVITY_PAGE) }, [selectedId])

  /**
   * La URL manda sobre qué ficha está abierta, incluida la ausencia de ficha.
   *
   * Ignorar el caso nulo dejaba el botón "atrás" a medias: la barra de
   * direcciones volvía a la lista y la pantalla se quedaba en la ficha.
   */
  useEffect(() => {
    setSelectedId(initialContactId ?? '')
  }, [initialContactId])

  /** Igual que en Formularios: el aviso va aparte para que la identidad del
   *  callback no reponga `selectedId` en cada render del taller. */
  useEffect(() => {
    if (initialContactId) onConsumeInitialContact?.()
  }, [initialContactId, onConsumeInitialContact])
  const [addOpen, setAddOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [draft, setDraft] = useState({ name: '', email: '', phone: '', note: '' })
  const filterMenuRef = useRef<HTMLDivElement>(null)

  const debouncedQuery = useDebounced(query)
  /* Sigue visible mientras se despide, aunque el contacto ya haya llegado. */
  const contactLoader = useRecordLoader(Boolean(selectedId) && !selected)
  /* La lista tiene su propia espera: se llega a ella sin ningún contacto
     abierto, así que no puede compartir la de la ficha. */
  const listLoader = useRecordLoader(loading)
  const [starredOnly, setStarredOnly] = useState(false)
  /* Las estrellas, aquí arriba: de ellas sale el recuento que necesita la
     paginación, tres líneas más abajo. */
  const [starred, setStarred] = useState<Set<string>>(() => readStarredIds(projectId, 'contacts'))
  /* Con el filtro puesto, el recuento es el de lo que queda tras recortar; el
     `total` del servidor cuenta también los que no están destacados. */
  const starredTotal = contacts.reduce((count, row) => count + (starred.has(row.id) ? 1 : 0), 0)
  const paged = useServerPaged(
    starredOnly ? starredTotal : total,
    starredOnly ? SERVER_MAX_PAGE_SIZE : PAGE_SIZE,
    `${debouncedQuery}|${sourceFilter}|${statusFilter}|${accountFilter}|${sort}|${starredOnly}`,
  )
  const { page } = paged

  /* Con la lista vacía no sale la barra entera: ni buscador, ni filtros, ni
     el botón de crear. No hay nada que buscar ni que acotar, y crear ya lo
     ofrece el propio mensaje de empezar. Aquí la lista la recorta el
     SERVIDOR, así que vacía puede significar «no hay» o «no encontró»:
     mientras haya algo escrito o algún filtro puesto la barra se queda, o
     no habría cómo deshacer la búsqueda que dejó la pantalla en blanco. */
  const showToolbar = contacts.length > 0
    || query.trim() !== ''
    || sourceFilter !== 'all'
    || statusFilter !== 'active'
    || accountFilter !== 'all'
    || starredOnly

  const clearFilters = () => {
    setQuery('')
    setSourceFilter('all')
    setStatusFilter('active')
    setAccountFilter('all')
    setSort('lastDesc')
    setStarredOnly(false)
  }

  // La lista la arma el servidor: agrupa los envíos por correo, le monta encima
  // la ficha editada y devuelve sólo la página pedida. Antes se descargaba todo
  // para agruparlo aquí, y como los envíos venían topados a 200, quien sólo
  // aparecía en uno más antiguo no salía en la lista.
  useEffect(() => {
    let alive = true
    setLoading(true)
    listContacts(projectId, token, {
      page: starredOnly ? 1 : page + 1,
      pageSize: starredOnly ? SERVER_MAX_PAGE_SIZE : PAGE_SIZE,
      q: debouncedQuery,
      sort,
      status: statusFilter,
      source: sourceFilter,
      account: accountFilter,
    })
      .then((result) => {
        if (!alive) return
        setContacts(result.data)
        setTotal(result.total)
        setFailed(false)
      })
      .catch(() => { if (alive) setFailed(true) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [projectId, token, page, debouncedQuery, sort, statusFilter, sourceFilter, accountFilter, starredOnly, reloadKey])

  /** El detalle se pide por su cuenta: el enlace puede apuntar a la página 40. */
  useEffect(() => {
    if (!selectedId) { setSelected(null); return }
    let alive = true
    setDetailFailed(false)
    getContact(projectId, selectedId, token)
      .then((row) => { if (alive) setSelected(row) })
      .catch(() => {
        // No ocultar errores del servidor volviendo a la lista: la ficha puede
        // existir y fallar por una interrupción o un error de API. Mantener el
        // id permite reintentar y evita que el toque parezca no haber funcionado.
        if (!alive) return
        setDetailFailed(true)
      })
    return () => { alive = false }
  }, [projectId, token, selectedId, reloadKey, onRecordChange])

  useEffect(() => {
    if (!filterMenuOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (!filterMenuRef.current?.contains(event.target as Node)) setFilterMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setFilterMenuOpen(false) }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [filterMenuOpen])

  /** Exporta lo que estás viendo, no toda la base: el filtro es la selección. */
  const exportCsv = () => {
    downloadCsv(
      t('contacts.csv.filename'),
      [
        t('contacts.csv.name'),
        t('contacts.csv.email'),
        t('contacts.csv.phone'),
        t('contacts.csv.tags'),
        t('contacts.csv.status'),
        t('contacts.csv.source'),
        t('contacts.csv.forms'),
        t('contacts.csv.firstSeen'),
        t('contacts.csv.lastSeen'),
        t('contacts.csv.note'),
      ],
      visible.map((contact) => [
        contact.name,
        contact.email,
        contact.phone,
        contact.tags.join(' · '),
        t(`contacts.status.${contact.status}`),
        sourceLabel(contact.initialSource),
        contact.forms.join(' · '),
        contact.firstSeen,
        contact.lastSeen,
        contact.note,
      ]),
    )
  }

  // Buscar, filtrar y ordenar los resuelve la consulta; los destacados no, que
  // viven en este navegador. `visible` se mantiene como nombre porque el resto
  // del componente lo usa para pintar.
  const visible = starredOnly ? contacts.filter((row) => starred.has(row.id)) : contacts
  const listSelection = useListSelection(visible.map((row) => row.id))
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)

  const toggleStar = (ids: string[], next: boolean) => {
    setStarred(new Set(setStarredIds(projectId, 'contacts', ids, next)))
  }

  /**
   * Borra los seleccionados, en secuencia.
   *
   * No hay endpoint de borrado múltiple, y veinte peticiones a la vez contra el
   * mismo proyecto es la forma de que el servidor empiece a rechazar algunas. Si
   * alguna falla se sigue con el resto y se dice cuántas quedaron: parar a la
   * primera dejaría media selección borrada sin que nadie sepa cuál.
   */
  const removeSelected = async () => {
    const ids = listSelection.ids
    let done = 0
    for (const id of ids) {
      try {
        await deleteContact(projectId, id, token)
        done += 1
      } catch { /* se sigue con el resto */ }
    }
    listSelection.clear()
    setBulkDeleteOpen(false)
    openContact('')
    setReloadKey((key) => key + 1)
    if (done === ids.length) {
      showToast(done === 1 ? t('contacts.deletedOne') : t('contacts.deletedMany', { count: done }))
    } else {
      showToast(t('contacts.deletedPartial', { done, total: ids.length }), 'error')
    }
  }

  /* El hilo arranca con lo guardado. Una nota de las de antes —texto suelto, sin
     autor— entra como el primer comentario, firmada como del equipo: perderla
     por no tener forma de hilo sería tirar lo que alguien escribió. */
  const savedNote = selected?.note ?? ''
  useEffect(() => {
    setNoteItems(parseNotes(savedNote, t('overview.notes.unnamed')))
    setNoteDraft('')
    setNoteEditingId(null)
    setNoteDeletingId(null)
    setNotesFull(false)
    setNotesFailed(false)
    noteDeletedIds.current = new Set()
  }, [selectedId, savedNote, t])

  /**
   * Guardar es leer, fundir y escribir — igual que en el widget del equipo.
   *
   * La columna se reemplaza entera, así que mandar lo que hay en pantalla borra
   * lo que un compañero haya escrito mientras tanto. Se relee la ficha, se funde
   * (ver `mergeNotes`) y se manda el resultado. Si la relectura falla se sigue
   * con lo propio: perder el comentario recién escrito por un fallo de red sería
   * peor que el riesgo de pisar a alguien.
   */
  const persistNotes = (contactId: string, next: ProjectNoteItem[]) => {
    setNotesSaving(true)
    void (async () => {
      let merged = next
      try {
        const fresh = await getContact(projectId, contactId, token)
        merged = mergeNotes(parseNotes(fresh.note, t('overview.notes.unnamed')), next, noteDeletedIds.current)
      } catch {
        // Sin relectura, se guarda lo propio tal cual.
      }
      if (!notesFit(merged)) {
        setNotesSaving(false)
        setNotesFull(true)
        return
      }
      try {
        await updateContact(projectId, contactId, { note: serializeNotes(merged) }, token)
        setNoteItems(merged)
        setNotesFailed(false)
      } catch {
        setNotesFailed(true)
      }
      setNotesSaving(false)
    })()
  }

  const addNote = (contactId: string) => {
    const text = noteDraft.trim().slice(0, NOTE_TEXT_MAX)
    if (!text || notesSaving) return
    const note: ProjectNoteItem = {
      id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `note-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      userId: currentUser?.id ?? '',
      userName: currentUser?.name || t('overview.notes.unnamed'),
      userAvatar: currentUser?.avatarUrl ?? null,
      text,
      createdAt: new Date().toISOString(),
    }
    const next = [note, ...noteItems]
    /* El tope se comprueba ANTES de tocar la pantalla: si no cabe, el comentario
       no se da por escrito ni se limpia el cuadro, y se puede copiar a otro
       sitio antes de hacer hueco. */
    if (!notesFit(next)) {
      setNotesFull(true)
      return
    }
    setNoteItems(next)
    setNoteDraft('')
    setNotesFull(false)
    persistNotes(contactId, next)
  }

  const saveNoteEdit = (contactId: string, noteId: string) => {
    const text = noteEditText.trim().slice(0, NOTE_TEXT_MAX)
    if (!text) return
    const next = noteItems.map((item) => (
      item.id === noteId ? { ...item, text, updatedAt: new Date().toISOString() } : item
    ))
    if (!notesFit(next)) {
      setNotesFull(true)
      return
    }
    setNoteItems(next)
    setNoteEditingId(null)
    setNotesFull(false)
    persistNotes(contactId, next)
  }

  const deleteNote = (contactId: string, noteId: string) => {
    noteDeletedIds.current.add(noteId)
    const next = noteItems.filter((item) => item.id !== noteId)
    setNoteItems(next)
    setNoteDeletingId(null)
    setNotesFull(false)
    persistNotes(contactId, next)
  }

  /**
   * Toda la actividad de la persona en una sola línea, por fecha.
   *
   * Enseñaba sólo sus formularios, así que una ficha con tres compras y dos
   * citas decía «todavía no hay actividad». Aquí se juntan las cuatro fuentes
   * que la ficha ya trae —envíos, compras, citas y el hilo de comentarios— más
   * el día en que entró a la lista. Se arma en el panel y no en el servidor
   * porque los datos ya están aquí: pedir una consulta más sólo para ordenarlos
   * sería trabajo de ida y vuelta para nada.
   */
  type ContactTimelineEvent =
    | { kind: 'form'; id: string; at: string; item: NonNullable<typeof selected>['activities'][number] }
    | { kind: 'purchase' | 'refund'; id: string; at: string; item: ContactPurchase }
    | { kind: 'booking'; id: string; at: string; item: ContactBooking }
    | { kind: 'booking-change'; id: string; at: string; item: ContactBookingChange }
    | { kind: 'note'; id: string; at: string; item: ProjectNoteItem }
    | { kind: 'joined'; id: string; at: string }

  const timeline: ContactTimelineEvent[] = (() => {
    if (!selected) return []
    const events: ContactTimelineEvent[] = []
    for (const activity of selected.activities) {
      events.push({ kind: 'form', id: `form:${activity.id}`, at: activity.createdAt, item: activity })
    }
    for (const purchase of selected.purchases) {
      /* Un reembolso no es una compra más: es lo contrario, y se dice con sus
         palabras. La fecha sigue siendo la del cobro, que es la que ancla las
         dos cosas en el relato. */
      events.push({
        kind: purchase.status === 'refunded' ? 'refund' : 'purchase',
        id: `order:${purchase.id}`,
        at: purchase.createdAt,
        item: purchase,
      })
    }
    for (const booking of selected.bookings) {
      /* Cuándo la reservó, no cuándo es la cita: la línea cuenta lo que la
         persona hizo, y la fecha de la cita va en el cuerpo del evento. */
      events.push({ kind: 'booking', id: `booking:${booking.id}`, at: booking.createdAt || booking.startsAt, item: booking })
    }
    for (const change of selected.bookingChanges ?? []) {
      events.push({ kind: 'booking-change', id: `booking-change:${change.id}`, at: change.at, item: change })
    }
    for (const note of noteItems) {
      if (note.createdAt) events.push({ kind: 'note', id: `note:${note.id}`, at: note.createdAt, item: note })
    }
    if (selected.firstSeen) events.push({ kind: 'joined', id: 'joined', at: selected.firstSeen })
    /* Fechas ISO: en texto se ordenan igual que en el tiempo. */
    return events.sort((a, b) => b.at.localeCompare(a.at))
  })()

  /**
   * La tarjeta de un suceso.
   *
   * Vive fuera del `map` porque la pintan DOS listas: la cronología entera y la
   * pestaña de Reservas, que es la misma lista filtrada. Estaban escritas por
   * separado y habían divergido —la pestaña no enseñaba los cambios de horario,
   * y su resumen de la cita decía menos que el de Actividad—, así que la misma
   * reserva se veía de dos maneras según la pestaña.
   */
  const renderEvent = (event: ContactTimelineEvent) => {
    /* Dentro del `map` esto quedaba bajo la comprobación de la ficha abierta; en
       una función suelta hay que repetirla. Sin ficha no hay nada que pintar. */
    if (!selected) return null
    return (
                    <li
                      className={`dash-cp-event${event.kind === 'booking' || event.kind === 'booking-change' || event.kind === 'note' || event.kind === 'form' || event.kind === 'purchase' || event.kind === 'refund' ? ' is-booking-link' : ''}${event.kind === 'form' && expandedAnswers === event.item.id ? ' is-open' : ''}`}
                      key={event.id}
                      {...(event.kind === 'note' ? {
                        role: 'button',
                        tabIndex: 0,
                        'aria-label': t('contacts.activity.openNote'),
                        onClick: () => openNote(event.item.id),
                        onKeyDown: (keyEvent: ReactKeyboardEvent<HTMLLIElement>) => {
                          if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                            keyEvent.preventDefault()
                            openNote(event.item.id)
                          }
                        },
                      } : event.kind === 'form' ? {
                        /* `aria-expanded` y no una etiqueta propia: el nombre
                           accesible ya lo pone el contenido de la tarjeta, y lo
                           que falta decir es si está abierta o cerrada. */
                        role: 'button',
                        tabIndex: 0,
                        'aria-expanded': expandedAnswers === event.item.id,
                        onClick: () => toggleAnswers(event.item),
                        onKeyDown: (keyEvent: ReactKeyboardEvent<HTMLLIElement>) => {
                          if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                            keyEvent.preventDefault()
                            toggleAnswers(event.item)
                          }
                        },
                      } : event.kind === 'purchase' || event.kind === 'refund' ? {
                        /* La compra entera abre su pedido, como una cita abre la
                           suya. Tenía un enlace pequeño bajo la fecha y el resto
                           de la tarjeta no respondía: se apuntaba a una línea de
                           texto en una caja de cien píxeles de alto. */
                        role: 'button',
                        tabIndex: 0,
                        'aria-label': t('contacts.activity.viewOrder'),
                        'aria-busy': orderLoading === event.item.id,
                        onClick: () => openOrder(event.item.id),
                        onKeyDown: (keyEvent: ReactKeyboardEvent<HTMLLIElement>) => {
                          if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                            keyEvent.preventDefault()
                            openOrder(event.item.id)
                          }
                        },
                      } : event.kind === 'booking' || event.kind === 'booking-change' ? {
                        role: 'button',
                        tabIndex: 0,
                        'aria-label': event.kind === 'booking'
                          ? t('contacts.activity.booked', { service: event.item.offerTitle || t('contacts.booking.deletedService') })
                          : t('contacts.activity.rescheduled', { service: event.item.offerTitle || t('contacts.booking.deletedService') }),
                        onClick: () => openBooking(event.kind === 'booking' ? event.item.id : event.item.bookingId),
                        // Adelantada: al pulsar, la cita ya suele estar (ver `fetchBooking`).
                        onPointerEnter: () => prefetchBooking(event.kind === 'booking' ? event.item.id : event.item.bookingId),
                        onFocus: () => prefetchBooking(event.kind === 'booking' ? event.item.id : event.item.bookingId),
                        onKeyDown: (keyEvent: ReactKeyboardEvent<HTMLLIElement>) => {
                          if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                            keyEvent.preventDefault()
                            openBooking(event.kind === 'booking' ? event.item.id : event.item.bookingId)
                          }
                        },
                      } : {})}
                    >
                      <span className="dash-cp-event-dot" aria-hidden="true" />
                      {/* También la nota lleva su icono aquí: esta columna dice
                          QUÉ pasó, y una cara en medio de una fila de iconos
                          rompía la lectura vertical. Quién la escribió se ve
                          igual, en pequeño y junto a su nombre, que es donde
                          la cara acompaña a algo que la nombra. */}
                      <ContactCardThumb
                        icon={event.kind === 'form' ? IconClipboardList
                          : event.kind === 'purchase' ? IconShoppingBag
                          : event.kind === 'refund' ? IconEventRefund
                          : event.kind === 'booking' ? IconCalendarEvent
                          : event.kind === 'booking-change' ? IconCalendarTime
                          : event.kind === 'note' ? IconNote
                          : IconEventJoined}
                        tone={event.kind === 'form' ? 'form'
                          : event.kind === 'purchase' || event.kind === 'refund' ? 'purchase'
                          : event.kind === 'booking' || event.kind === 'booking-change' ? 'booking'
                          : event.kind === 'note' ? 'note'
                          : 'contact'}
                      />
                      <div className="dash-cp-event-body">
                        <div className="dash-cp-event-main">
                        <div className="dash-cp-event-head">
                          <span className="dash-cp-event-copy">
                            {/* El importe pegado al título, no al otro extremo
                                de la fila: «Compró X» y lo que costó son la
                                misma frase, y separados por el ancho de la
                                tarjeta había que leer de ida y vuelta. */}
                            <span className="dash-cp-event-title-row">
                            {/* La cara de quien la movió, junto a su nombre. Sólo
                                cuando lo movió alguien del equipo: en un cambio
                                del cliente no hay cuenta que retratar. */}
                            {event.kind === 'booking-change' && event.item.actorKind !== 'customer' && changeActor(event.item).name ? (
                              <NoteAvatar name={changeActor(event.item).name} url={changeActor(event.item).avatarUrl} />
                            ) : null}
                            {/* La cara de quien dejó la nota, del tamaño de la
                                línea: acompaña al «Nota de Heriberto», no lo
                                encabeza. Sin autor no hay cara — las notas
                                viejas no guardaron quién las dejó. */}
                            {event.kind === 'note' && noteAuthor(event.item).name ? (
                              <NoteAvatar name={noteAuthor(event.item).name} url={noteAuthor(event.item).avatarUrl} />
                            ) : null}
                            <strong>
                              {event.kind === 'form' ? t('contacts.activity.sentForm', { form: event.item.formName })
                                : event.kind === 'purchase' ? t('contacts.activity.bought', { offer: event.item.offerTitle || t('contacts.purchase.deletedOffer') })
                                : event.kind === 'refund' ? t('contacts.activity.refunded', { offer: event.item.offerTitle || t('contacts.purchase.deletedOffer') })
                                : event.kind === 'booking' ? t('contacts.activity.booked', { service: event.item.offerTitle || t('contacts.booking.deletedService') })
                                : event.kind === 'booking-change' ? (event.item.actorKind === 'customer'
                                  ? t('contacts.activity.rescheduledCustomer', { service: event.item.offerTitle || t('contacts.booking.deletedService') })
                                  : changeActor(event.item).name
                                    ? t('contacts.activity.rescheduledBy', { name: changeActor(event.item).name, service: event.item.offerTitle || t('contacts.booking.deletedService') })
                                    : t('contacts.activity.rescheduledTeam', { service: event.item.offerTitle || t('contacts.booking.deletedService') }))
                                /* Con autor, quién la escribió; sin autor, lo que
                                   es. «Nota de un miembro del equipo» nombraba a
                                   alguien que no existe: las notas viejas no
                                   guardaban quién las dejó. */
                                : event.kind === 'note' ? (noteAuthor(event.item).name
                                  ? t('contacts.activity.note', { author: noteAuthor(event.item).name })
                                  : t('contacts.activity.internalNote'))
                                : t('contacts.activity.joined')}
                            </strong>
                            {event.kind === 'purchase' || event.kind === 'refund' ? (
                              <span className="dash-cp-purchase-amount">{event.item.formatted}</span>
                            ) : null}
                            </span>
                            {event.kind !== 'booking-change' && event.kind !== 'note' ? <small>
                              {event.kind === 'form' ? (
                                <>{answerCountLabel(answers(event.item, t('contacts.answer')).length)}{event.item.pageUrl ? <> · <span title={event.item.pageUrl}>{compactPageUrl(event.item.pageUrl)}</span></> : null}</>
                              ) : event.kind === 'purchase' || event.kind === 'refund' ? (
                                <>{purchaseStatusLabel(event.item.status)} · {t('contacts.purchase.reference', { reference: shortReference(event.item.id) })}</>
                              ) : event.kind === 'booking' ? (
                                <>{event.item.resourceName || t('contacts.booking.noResource')} · {bookingDurationLabel(event.item)}</>
                              ) : event.kind === 'joined' ? (
                                /* El origen en su propia línea, como el resto de
                                   segundas líneas: pegado al título con un punto
                                   medio, «Entró a contactos · Compra» se leía
                                   como una sola frase larga. */
                                sourceLabel(selected.initialSource)
                              ) : null}
                            </small> : null}
                          </span>
                        </div>

                        {event.kind === 'form' && expandedAnswers === event.item.id && answers(event.item, t('contacts.answer')).length ? (
                          <dl className="dash-cp-answers">
                            {answers(event.item, t('contacts.answer')).map(([label, value], index) => (
                              <div key={`${label}-${index}`}>
                                <dt>{label}</dt>
                                <dd>{value}</dd>
                              </div>
                            ))}
                          </dl>
                        ) : event.kind === 'booking' ? (
                          <p className="dash-cp-event-note">{bookingWhen(event.item, language)}</p>
                        ) : event.kind === 'booking-change' ? (
                          /* De dónde a dónde, en una línea con una flecha.
                             Eran dos recuadros rotulados «Fecha anterior» y
                             «Fecha nueva»: cuatro renglones y dos marcos para
                             decir un antes y un después que la flecha dice
                             sola. Los rótulos siguen para quien no ve la
                             flecha, en `.dash-sr-only`. */
                          <p className="dash-cp-change">
                            {event.item.previousStartsAt ? (
                              <>
                                <span className="dash-cp-change-from">
                                  <span className="dash-sr-only">{t('contacts.activity.previousDate')}: </span>
                                  {new Intl.DateTimeFormat(language, { timeZone: event.item.timezone, dateStyle: 'medium', timeStyle: 'short' }).format(new Date(event.item.previousStartsAt))}
                                </span>
                                <IconArrowNarrowRight size={15} className="dash-cp-change-arrow" aria-hidden="true" />
                              </>
                            ) : null}
                            <span className="dash-cp-change-to">
                              <span className="dash-sr-only">{t('contacts.activity.newDate')}: </span>
                              {new Intl.DateTimeFormat(language, { timeZone: event.item.timezone, dateStyle: 'medium', timeStyle: 'short' }).format(new Date(event.item.startsAt))}
                            </span>
                          </p>
                        /* Una compra no lleva cuerpo: el estado y el número de
                           pedido ya están en la línea de debajo del título, y
                           repetidos abajo el evento decía dos veces lo mismo. */
                        ) : event.kind === 'note' ? (
                            <p className="dash-cp-event-note">{event.item.text}</p>
                        ) : null}

                        </div>
                          <div className="dash-cp-event-trailing">
                            {/*
                              * La fecha PRIMERO, a la altura del título.
                              *
                              * Iba debajo de las pastillas y centrada en la
                              * tarjeta, así que su sitio dependía de lo que hubiera
                              * encima: en una cita quedaba abajo, en una compra en
                              * medio y en un mensaje leído arriba. Tres tarjetas
                              * seguidas, tres alturas para el mismo dato. Ahora
                              * es siempre la primera línea de la derecha, frente
                              * al título, y lo que cambia según el caso —el estado
                              * de la cita, «Nuevo», la pista de abrir— va debajo.
                              *
                              * La hora acompaña siempre a la fecha: la actividad
                              * es un historial y dos sucesos del mismo día se
                              * distinguen precisamente por cuándo ocurrieron.
                              */}
                            <time dateTime={event.at} title={fmt.dateTime(event.at)}>
                              {formatDashboardListDateWithTime(event.at)}
                            </time>
                            {/* Sólo lo que pide atención: «Nuevo» mientras no se
                                ha abierto, y «Spam». «Leído» era el caso normal
                                —casi todos los mensajes lo son— y poner una
                                pastilla gris en cada tarjeta para decir «nada que
                                ver aquí» apagaba la que sí importa. */}
                            {event.kind === 'form' && event.item.status !== 'read' ? (
                              <span className={`dash-contact-status is-${event.item.status}`}>
                                {t(`contacts.message.status.${event.item.status}`)}
                              </span>
                            ) : null}
                            {event.kind === 'booking' ? (
                              <>
                                {/* La marca de vencida viene de la fila que esta
                                    tarjeta sustituye: una cita pasada que sigue
                                    «Confirmada» es la que nadie cerró, y perderla
                                    al unificar habría quitado el único aviso de que
                                    queda algo por hacer. */}
                                <BookingOverdueMark booking={event.item} label={t('contacts.booking.overdue')} />
                                <span className={`dash-booking-status ${BOOKING_STATUS_TONE[event.item.status as keyof typeof BOOKING_STATUS_TONE] ?? ''}`}>
                                  {t(`contacts.booking.status.${event.item.status}`)}
                                </span>
                              </>
                            ) : null}
                            {/* Bajo la fecha, en la misma columna: el resto del
                                cobro —recibo de Stripe, tarjeta, comisión, neto,
                                devoluciones— vive en su ficha de Pedidos. Se
                                enlaza en vez de copiarse: esos datos se le piden
                                a Stripe al abrirla, y una copia aquí envejecería
                                sin avisar. */}
                            {/* Ya no es un botón: lo es la tarjeta entera, y un
                                botón dentro de otro dispararía el clic dos
                                veces. Queda como pista de lo que pasa al pulsar,
                                y sólo al acercarse — en reposo desequilibraba la
                                columna, con una línea de enlace azul en cada
                                compra y nada en el resto de tarjetas. */}
                            {event.kind === 'purchase' || event.kind === 'refund' ? (
                              <span className="dash-cp-event-link is-hover-hint" aria-hidden="true">
                                {t('contacts.activity.viewOrder')} <IconEventGo size={13} />
                              </span>
                            ) : null}
                          </div>
                      </div>
                    </li>
    )
  }

  /**
   * Abrir una nota desde la cronología.
   *
   * Editarla ahí mismo era meter un formulario en una lista que sólo cuenta lo
   * que pasó, y los botones de editar y borrar no tenían dónde ponerse sin
   * estorbar. Ahora la cronología enlaza y quien edita es Notas, que es su
   * pantalla: se cambia de pestaña, se señala la nota y se lleva la vista.
   *
   * El desplazamiento espera un fotograma porque la pestaña todavía no está
   * pintada: sin esperar, no hay nodo al que ir.
   */
  const openNote = (noteId: string) => {
    setProfileTab('notes')
    setHighlightedNote(noteId)
    requestAnimationFrame(() => {
      document.getElementById(`cp-note-${noteId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  /* El resalte se apaga solo a los tres segundos, y el temporizador se cancela
     si entre medias se salta a otra nota: si no, el de la primera apagaría el de
     la segunda. */
  useEffect(() => {
    if (!highlightedNote) return
    const timer = window.setTimeout(() => setHighlightedNote(null), 3000)
    return () => window.clearTimeout(timer)
  }, [highlightedNote])

  /* Reservas: lo mismo que Actividad, sin lo que no es una cita. Así una cita
     movida aparece también aquí, que es donde se la busca. */
  const bookingEvents = timeline.filter((event) => event.kind === 'booking' || event.kind === 'booking-change')
  /* Y los envíos, por lo mismo: la pestaña de Formularios es esta cronología
     filtrada, no una lista aparte. Ver la nota de la propia pestaña. */
  const formEvents = timeline.filter((event) => event.kind === 'form')
  /* Y las compras. Un reembolso ya viene como su propio `kind` sobre la MISMA
     compra —no como un evento más—, así que filtrar las dos da una tarjeta por
     compra, igual que la lista que sustituye. */
  const purchaseEvents = timeline.filter((event) => event.kind === 'purchase' || event.kind === 'refund')

  const tabCount = (tab: ProfileTab): number => {
    if (!selected) return 0
    if (tab === 'activity') return timeline.length
    if (tab === 'forms') return formEvents.length
    if (tab === 'purchases') return purchaseEvents.length
    /* Cuenta lo que la pestaña enseña: citas Y sus cambios de horario. */
    if (tab === 'bookings') return bookingEvents.length
    if (tab === 'memberships') return 0
    return noteItems.length
  }

  // Flechas para moverse entre pestañas, como espera un tablist.
  const onTabKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    event.preventDefault()
    const index = profileTabs.findIndex((tab) => tab.id === profileTab)
    const next = profileTabs[(index + step + profileTabs.length) % profileTabs.length]!
    setProfileTab(next.id)
    document.getElementById(`cp-tab-${next.id}`)?.focus()
  }

  const toggleSort = (column: 'name' | 'email' | 'source' | 'first' | 'last') => {
    if (column === 'name') setSort((current) => current === 'nameAsc' ? 'nameDesc' : 'nameAsc')
    if (column === 'email') setSort((current) => current === 'emailAsc' ? 'emailDesc' : 'emailAsc')
    if (column === 'source') setSort((current) => current === 'sourceAsc' ? 'sourceDesc' : 'sourceAsc')
    if (column === 'first') setSort((current) => current === 'firstDesc' ? 'firstAsc' : 'firstDesc')
    if (column === 'last') setSort((current) => current === 'lastDesc' ? 'lastAsc' : 'lastDesc')
  }

  const saveContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!draft.name.trim() || !draft.email.trim() || saving) return
    setSaving(true)
    setSaveError('')
    try {
      const contact = await createContact(projectId, {
        name: draft.name.trim(),
        email: draft.email.trim(),
        phone: draft.phone.trim(),
        note: draft.note.trim(),
      }, token)
      setReloadKey((key) => key + 1)
      openContact(contact.email.trim() ? `email:${contact.email.trim().toLocaleLowerCase()}` : `manual:${contact.id}`)
      setDraft({ name: '', email: '', phone: '', note: '' })
      setAddOpen(false)
    } catch (error) {
      setSaveError(String(error).includes('contact_limit')
        ? t('contacts.add.limitError')
        : t('contacts.add.saveError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
    {/* Misma razón que en Formularios: la ficha se pide aparte, y sin esta rama
        se veía la lista entera un instante antes de la persona que se abrió. */}
    {selectedId && detailFailed ? (
      <div className="dash-projects-view dash-pages-view dash-contact-record-view dash-loaded-content-in">
        <DashboardPageHeader
          title={t('contacts.detailLoadFailed')}
          actions={<button type="button" className="dash-secondary-btn dash-detail-back" onClick={() => openContact('')}><IconArrowLeft size={14} /> {t('contacts.back')}</button>}
        />
        <div className="dash-contact-detail-error">
          <p>{t('contacts.detailLoadFailedBody')}</p>
          <button type="button" className="dash-primary-btn" onClick={() => { setDetailFailed(false); setReloadKey((key) => key + 1) }}>{t('common.tryAgain')}</button>
        </div>
      </div>
    ) : selectedId && contactLoader.visible ? (
      <SectionLoader loader={contactLoader} label={t('contacts.loadingDetail')} />
    ) : selected ? (
      <div className="dash-projects-view dash-pages-view dash-contact-record-view dash-loaded-content-in">
        <DashboardPageHeader
          title={selected.name || t('contacts.unnamed')}
          subtitle={selected.email || selected.phone || t('contacts.detail.subtitle')}
          actions={(
            <div className="dash-cp-header-actions">
              <button type="button" className="dash-secondary-btn dash-detail-back" onClick={() => openContact('')}><IconArrowLeft size={14} /> {t('contacts.back')}</button>
              {/* Dar una membresía, un paquete o un producto digital: sólo a quien puede (ver la tarjeta Accesos). */}
              {canGrantAccess ? (
                <button type="button" className="dash-secondary-btn" onClick={() => setGrantSignal((value) => value + 1)}><IconGift size={14} /> {t('contacts.access.give')}</button>
              ) : null}
              {/* Lo que antes vivía al final de la columna («Acciones»): a la
                  vista, en un menú, con lo destructivo separado y en rojo. */}
              <ContactMoreMenu
                labels={{
                  more: t('contacts.moreActions'),
                  spam: selected.status === 'spam' ? t('contacts.unspam') : t('contacts.markSpam'),
                  block: selected.status === 'blocked' ? t('contacts.unblock') : t('contacts.block'),
                  remove: t('contacts.deleteContact'),
                }}
                busy={savingField === 'status'}
                onSpam={() => { void patchContact(selected, { status: selected.status === 'spam' ? 'active' : 'spam' }, 'status') }}
                onBlock={() => { void patchContact(selected, { status: selected.status === 'blocked' ? 'active' : 'blocked' }, 'status') }}
                onDelete={() => setContactToDelete(selected)}
              />
            </div>
          )}
        />
        {/* Dos columnas al estilo de una ficha de cliente: a la izquierda lo que
            pasó (se lee en orden), a la derecha quién es (se consulta de un
            vistazo). Antes todo era una sola columna y los datos del contacto
            quedaban enterrados bajo el historial. */}
        <div className="dash-cp-grid">
          <aside className="dash-cp-side">
            <section className="dash-cp-card">
              <div className="dash-cp-card-head">
                <h3><span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-cp-head-icon is-fog" aria-hidden="true"><IconUser size={14} /></span> {t('contacts.detail.contact')}</h3>
              </div>
              <div className="dash-cp-identity">
                <span className="dash-contact-avatar dash-contact-avatar--large">{selected.avatarUrl && /^https?:/.test(selected.avatarUrl) ? <img src={selected.avatarUrl} alt="" /> : initials(selected)}</span>
                <div>
                  <span className="dash-cp-editable"><strong>{selected.name || t('contacts.unnamed')}</strong><button type="button" className="dash-cp-edit-inline" aria-label={t('contacts.edit')} data-tooltip={t('contacts.edit')} onClick={() => setEditOpen(true)}><IconPencil size={13} /></button></span>
                  {/* El estado se afirma aquí. Deducirlo de la etiqueta de un
                      botón ("Quitar de spam" ⇒ está en spam) obliga a razonar al
                      revés cada vez que abres la ficha. */}
                  <div className="dash-cp-identity-badges">
                    {selected.hasAccount ? <AccountBadge label={t('contacts.account.badge')} hint={t('contacts.account.badgeHint')} /> : null}
                    <span className={`dash-cp-state is-${selected.status}`}>
                      {selected.status === 'spam' ? <><IconBan size={11} /> {t('contacts.status.spam')}</>
                        : selected.status === 'blocked' ? <><IconShieldOff size={11} /> {t('contacts.status.blocked')}</>
                        : <><IconCheck size={11} /> {t('contacts.status.active')}</>}
                    </span>
                  </div>
                </div>
              </div>
              <dl className="dash-cp-fields">
                <div>
                  <dt><IconMail size={13} /> {t('contacts.email')}</dt>
                  {/* El botón sólo aparece al pasar el cursor: en reposo la ficha
                      se lee como datos, no como una fila de controles. */}
                  <dd className="dash-cp-copyable">
                    {selected.email ? (
                      <>
                        <a href={`mailto:${selected.email}`}>{selected.email}</a>
                        <button
                          type="button"
                          aria-label={t('contacts.copyEmailFor', { name: selected.name || t('contacts.thisContact') })}
                          data-tooltip={t('contacts.copyEmail')}
                          onClick={() => { void navigator.clipboard?.writeText(selected.email); showToast(t('contacts.emailCopied')) }}
                        >
                          <IconCopy size={13} />
                        </button>
                      </>
                    ) : <em>{t('contacts.noEmail')}</em>}
                    <button type="button" className="dash-cp-edit-inline" aria-label={t('contacts.edit')} data-tooltip={t('contacts.edit')} onClick={() => setEditOpen(true)}><IconPencil size={13} /></button>
                  </dd>
                </div>
                <div>
                  <dt><IconPhone size={13} /> {t('contacts.phone')}</dt>
                  <dd className="dash-cp-editable">{selected.phone ? <a href={`tel:${selected.phone}`}>{formatContactPhone(selected.phone)}</a> : <em>{t('contacts.noPhone')}</em>}<button type="button" className="dash-cp-edit-inline" aria-label={t('contacts.edit')} data-tooltip={t('contacts.edit')} onClick={() => setEditOpen(true)}><IconPencil size={13} /></button></dd>
                </div>
                <div>
                  <dt><IconTag size={13} /> {t('contacts.tags')}</dt>
                  <dd>
                    <PickerField
                      id="contact-tags"
                      texts={CONTACT_TAG_TEXTS}
                      max={20}
                      maxLength={40}
                      value={selected.tags}
                      known={knownTags}
                      /* Al momento en la ficha, sin esperar a que vuelva a cargar:
                         dos etiquetas seguidas no se pisan. */
                      onChange={(tags) => { setSelected({ ...selected, tags }); void patchContact(selected, { tags }, 'tags') }}
                      removeEverywhere={(name) => removeContactTag(projectId, name, token).then((result) => ({ count: result.contacts }))}
                      onRemovedEverywhere={() => setReloadKey((key) => key + 1)}
                    />
                  </dd>
                </div>
              </dl>
            </section>

            {/* Su cuenta y membresías, justo debajo de los datos del contacto. */}
            <ContactAccountCard projectId={projectId} identity={selected.id} token={token} canWrite={can(projectRole, 'contacts.write')} />

            {/* Lo que el equipo le dio sin cobro (membresía, paquete o digital). */}
            <ContactAccessCard projectId={projectId} identity={selected.id} name={selected.name ?? ''} token={token} openSignal={grantSignal} onCanGrant={setCanGrantAccess} listHidden />

            <section className="dash-cp-card">
              <div className="dash-cp-card-head"><h3><span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-cp-head-icon is-fog" aria-hidden="true"><IconMapPin size={14} /></span> {t('contacts.origin.title')}</h3></div>
              <dl className="dash-cp-stats">
                <div>
                  <dt>{t('contacts.initialSource')}</dt>
                  <dd>
                    <span className={`dash-contact-source is-${selected.initialSource}`}>
                      {sourceLabel(selected.initialSource)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>{t('contacts.firstSeen')}</dt>
                  {/* Sólo la fecha, como «Última interacción»: la hora exacta del
                      primer contacto no cambia ninguna decisión, y en la columna
                      hacía que el dato saltara de línea. */}
                  <dd title={fmt.dateTime(selected.firstSeen)}>{formatDashboardListDate(selected.firstSeen)}</dd>
                </div>
              </dl>
            </section>
          </aside>
          <div className="dash-cp-main">
            {/* Las cifras, arriba y a lo ancho: es lo primero que se mira de un
                cliente. En la columna estrecha eran una lista larga más. */}
            <dl className="dash-cp-kpis">
              <div><dt>{t('contacts.summary.purchases')}</dt><dd>{selected.purchases.length}</dd></div>
              <div><dt>{t('contacts.summary.totalValue')}</dt><dd>{purchaseTotal(selected.purchases)}</dd></div>
              <div><dt>{t('contacts.summary.bookings')}</dt><dd>{selected.bookings.length}</dd></div>
              <div><dt>{t('contacts.summary.forms')}</dt><dd>{selected.activities.length}</dd></div>
              <div><dt>{t('contacts.lastSeen')}</dt><dd title={fmt.dateTime(selected.lastSeen)}>{formatDashboardListDate(selected.lastSeen)}</dd></div>
            </dl>
            <section className="dash-cp-card">
              {/* Pestañas y no tarjetas apiladas: son cuatro vistas del mismo
                  contacto y sólo se mira una a la vez. Apiladas obligaban a
                  hacer scroll para descubrir que casi todas estaban vacías. */}
              {/* La franja sigue siendo la cabecera de la tarjeta; dentro va el
                  control de pestañas de los catálogos, con su pastilla blanca. */}
              <div className="dash-cp-tabs">
                <div
                  className="dash-booking-service-tabs dash-tabs-icon-collapse"
                  ref={setTabsEl}
                  role="tablist"
                  aria-label={t('contacts.detail.tabsAria')}
                  onKeyDown={onTabKeyDown}
                >
                  <span className="dash-booking-service-tabs-thumb" aria-hidden="true" />
                  {profileTabs.map((tab) => (
                    <button
                      key={tab.id}
                      id={`cp-tab-${tab.id}`}
                      type="button"
                      role="tab"
                      aria-label={tab.label}
                      aria-selected={profileTab === tab.id}
                      aria-controls={`cp-panel-${tab.id}`}
                      tabIndex={profileTab === tab.id ? 0 : -1}
                      className={profileTab === tab.id ? 'is-active' : ''}
                      onClick={() => setProfileTab(tab.id)}
                    >
                      <tab.icon size={14} aria-hidden="true" />
                      <span>{tab.label}</span>
                      {tabCount(tab.id) > 0 ? <span className="dash-cp-tab-count">{tabCount(tab.id)}</span> : null}
                    </button>
                  ))}
                </div>
              </div>

              <div id={`cp-panel-${profileTab}`} role="tabpanel" aria-labelledby={`cp-tab-${profileTab}`}>
                {profileTab === 'activity' ? (
                  timeline.length ? (
                    <>
                      <ol className="dash-cp-timeline">
                        {(isPhone ? timeline.slice(0, activityShown) : timeline).map(renderEvent)}
                      </ol>
                      {isPhone && timeline.length > activityShown ? (
                        <div className="dash-cp-show-more">
                          <button type="button" className="dash-secondary-btn" onClick={() => setActivityShown((count) => count + ACTIVITY_PAGE)}>
                            <IconChevronDown size={14} aria-hidden="true" /> {t('contacts.activity.showMore')}
                          </button>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="dash-cp-empty">
                      <IconActivity size={18} />
                      <p>{t('contacts.activity.empty')}</p>
                    </div>
                  )
                ) : profileTab === 'forms' ? (
                  /*
                   * La MISMA tarjeta que en Actividad, con el mismo `renderEvent`.
                   *
                   * Tenía su propia fila —icono, título, fecha y estado en una
                   * línea pulsable— que decía lo mismo con otra forma: dos
                   * maneras de ver el mismo envío según la pestaña desde la que
                   * se llegara. Es el arreglo que ya se le hizo a Reservas y por
                   * el mismo motivo: escritas por separado, divergen.
                   *
                   * Sigue habiendo una tarjeta por ENVÍO y no por formulario: si
                   * alguien escribe dos veces son dos mensajes distintos, y
                   * agruparlos bajo un contador escondía el segundo.
                   */
                  formEvents.length > 0 ? (
                    <ol className="dash-cp-timeline">
                      {formEvents.map(renderEvent)}
                    </ol>
                  ) : (
                    <div className="dash-cp-empty">
                      <IconClipboardList size={18} />
                      <p>{t('contacts.forms.empty')}</p>
                    </div>
                  )
                ) : profileTab === 'bookings' ? (
                  /* La MISMA tarjeta que en Actividad, con el mismo `renderEvent`.
                     Esta pestaña tenía su propia fila, más pobre, y además sólo
                     listaba citas: un cambio de horario no aparecía por ninguna
                     parte aunque es lo que se viene a mirar cuando alguien
                     pregunta «¿al final cuándo era?». Ahora es la cronología
                     filtrada, así que no puede volver a divergir. */
                  bookingEvents.length > 0 ? (
                    <ol className="dash-cp-timeline">
                      {bookingEvents.map(renderEvent)}
                    </ol>
                  ) : (
                    <div className="dash-cp-empty">
                      <IconCalendarEvent size={18} />
                      <p>{t('contacts.booking.empty')}</p>
                    </div>
                  )
                ) : profileTab === 'memberships' ? (
                  <div className="dash-cp-memberships-panel">
                    <ContactAccountCard projectId={projectId} identity={selected.id} token={token} canWrite={can(projectRole, 'contacts.write')} mode="memberships" />
                    <ContactAccessCard projectId={projectId} identity={selected.id} name={selected.name ?? ''} token={token} />
                  </div>
                ) : profileTab === 'purchases' ? (
                  /* La MISMA tarjeta que en Actividad, con el mismo
                     `renderEvent`, como ya hacen Formularios y Reservas. Esta
                     pestaña tenía su propia copia —decía «la misma tarjeta» y
                     era otra—, así que cualquier cambio de la de Actividad había
                     que acordarse de repetirlo aquí. */
                  purchaseEvents.length > 0 ? (
                    <ol className="dash-cp-timeline">
                      {purchaseEvents.map(renderEvent)}
                    </ol>
                  ) : (
                    <div className="dash-cp-empty">
                      <IconShoppingBag size={18} />
                      <p>{t('contacts.purchase.empty')}</p>
                    </div>
                  )
                ) : (
                  /* El hilo de la ficha: se escribe arriba y lo publicado baja
                     en orden, cada comentario con quién lo dejó y cuándo. Las
                     mismas piezas que el widget de notas del equipo. */
                  <div className="dash-notes-feed-widget dash-cp-notes-feed">
                    <div className={`dash-notes-composer${noteDraft.trim() ? ' has-draft' : ''}`}>
                      <div className="dash-notes-composer-row">
                        <div className="dash-notes-composer-input-box">
                          <textarea
                            className="dash-notes-composer-textarea"
                            placeholder={t('contacts.notes.placeholder')}
                            value={noteDraft}
                            maxLength={NOTE_TEXT_MAX}
                            rows={1}
                            onChange={(event) => { setNoteDraft(event.target.value); setNotesFull(false) }}
                            onKeyDown={(event) => {
                              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                                event.preventDefault()
                                addNote(selected.id)
                              }
                            }}
                          />
                          <div className="dash-notes-composer-bar">
                            <span className="dash-notes-kbd-hint">{t('overview.notes.keyboardHint', { key: COMMAND_KEY })}</span>
                            <button
                              type="button"
                              className="dash-notes-submit-btn"
                              disabled={!noteDraft.trim() || notesSaving}
                              onClick={() => addNote(selected.id)}
                            >
                              <IconNoteSend size={13} />
                              <span>{t('overview.notes.add')}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {notesFull ? (
                      <div className="dash-notes-error-pill">{t('contacts.notes.full')}</div>
                    ) : notesFailed ? (
                      <div className="dash-notes-error-pill">{t('overview.notes.saveFailed')}</div>
                    ) : null}

                    <div className="dash-notes-stream">
                      {noteItems.map((note) => {
                        const isMe = Boolean(currentUser?.id && note.userId === currentUser.id)
                        const isEditing = noteEditingId === note.id
                        const canManage = canManageNote(note)
                        return (
                          <article
                            key={note.id}
                            /* El id es el ancla del salto desde la cronología. */
                            id={`cp-note-${note.id}`}
                            className={`dash-note-card${highlightedNote === note.id ? ' is-highlighted' : ''}`}
                          >
                            <div className="dash-note-card-head">
                              <div className="dash-note-card-author">
                                <NoteAvatar name={noteAuthor(note).name} url={noteAuthor(note).avatarUrl} />
                                <span className="dash-note-author-name">{noteAuthor(note).name || t('overview.notes.unnamed')}</span>
                                {isMe ? <span className="dash-note-you-pill">{t('overview.notes.you')}</span> : null}
                                {note.createdAt ? (
                                  <>
                                    <span className="dash-note-dot">·</span>
                                    <time className="dash-note-time" dateTime={note.createdAt} title={fmt.dateTime(note.createdAt)}>
                                      {relativeNoteTime(note.createdAt)} · {formatDashboardListDay(note.createdAt)}
                                    </time>
                                  </>
                                ) : null}
                                {note.updatedAt ? <span className="dash-note-edited">({t('overview.notes.edited')})</span> : null}
                              </div>

                              {!isEditing && canManage ? (
                                <div className="dash-note-card-actions">
                                  {noteDeletingId === note.id ? (
                                    <div className="dash-note-confirm-delete">
                                      <span>{t('overview.notes.deleteConfirm')}</span>
                                      <button
                                        type="button"
                                        className="dash-note-btn-danger"
                                        aria-label={t('overview.notes.delete')}
                                        title={t('overview.notes.delete')}
                                        onClick={() => deleteNote(selected.id, note.id)}
                                      >
                                        <IconNoteCheck size={15} />
                                      </button>
                                      <button
                                        type="button"
                                        className="dash-note-btn-ghost"
                                        aria-label={t('overview.notes.cancel')}
                                        title={t('overview.notes.cancel')}
                                        onClick={() => setNoteDeletingId(null)}
                                      >
                                        <IconNoteX size={15} />
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        className="dash-note-action-btn"
                                        aria-label={t('overview.notes.edit')}
                                        title={t('overview.notes.edit')}
                                        onClick={() => { setNoteEditingId(note.id); setNoteEditText(note.text); setNoteDeletingId(null) }}
                                      >
                                        <IconNotePencil size={16} />
                                      </button>
                                      <button
                                        type="button"
                                        className="dash-note-action-btn is-danger"
                                        aria-label={t('overview.notes.delete')}
                                        title={t('overview.notes.delete')}
                                        onClick={() => setNoteDeletingId(note.id)}
                                      >
                                        <IconNoteTrash size={16} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              ) : null}
                            </div>

                            {isEditing ? (
                              <div className="dash-note-edit-area">
                                <textarea
                                  className="dash-note-edit-textarea"
                                  value={noteEditText}
                                  maxLength={NOTE_TEXT_MAX}
                                  rows={3}
                                  autoFocus
                                  onChange={(event) => setNoteEditText(event.target.value)}
                                  onKeyDown={(event) => {
                                    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                                      event.preventDefault()
                                      saveNoteEdit(selected.id, note.id)
                                    }
                                    if (event.key === 'Escape') setNoteEditingId(null)
                                  }}
                                />
                                <div className="dash-note-edit-bar">
                                  <span className="dash-notes-kbd-hint">{t('overview.notes.editHint', { key: COMMAND_KEY })}</span>
                                  <div className="dash-note-edit-btns">
                                    <button type="button" className="dash-notes-btn-secondary" onClick={() => setNoteEditingId(null)}>
                                      {t('overview.notes.cancel')}
                                    </button>
                                    <button
                                      type="button"
                                      className="dash-notes-submit-btn"
                                      disabled={!noteEditText.trim() || notesSaving}
                                      onClick={() => saveNoteEdit(selected.id, note.id)}
                                    >
                                      {t('overview.notes.save')}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="dash-note-card-body">{note.text}</div>
                            )}
                          </article>
                        )
                      })}
                      {noteItems.length === 0 ? (
                        <div className="dash-cp-notes-empty">
                          <ContactCardThumb icon={IconNote} tone="note" />
                          <span>
                            <strong>{t('contacts.notes.emptyTitle')}</strong>
                            <small>{t('contacts.notes.emptyBody')}</small>
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    ) : listLoader.visible ? (
      /* La espera sustituye a la pantalla ENTERA: sin cabecera, sin barra y sin
         tabla, porque una pantalla a medio pintar mueve sus piezas cada vez que
         llega una. Ver `SectionLoader`. */
      <SectionLoader loader={listLoader} label={t('contacts.loadingList')} />
    ) : (
      <div className="dash-projects-view dash-pages-view dash-contacts-list-view dash-loaded-content-in">
        <DashboardPageHeader
          title={t('contacts.title')}
          subtitle={t('contacts.subtitle')}
          actionLayout="split"
          actions={(
            <>
              <HeaderRefreshButton refreshing={loading} onRefresh={() => setReloadKey((key) => key + 1)} />
              <PlanUsageSummary items={[{ label: t('contacts.title'), used: total, limit: SITE_PLAN_LIMITS[plan].contacts }]} plan={plan} onOpenPlans={onOpenPlans} />
            </>
          )}
        />
        {showToolbar ? (
          <header className="dash-toolbar dash-layered-toolbar">
            {/* «Actualizar» y el buscador van juntos, en un solo hijo: la barra
                reparte con `space-between`, y con tres el buscador se iba al medio y
                los filtros caían a un segundo renglón. */}
            <div className="dash-toolbar-lead">
              <div className="dash-toolbar-search"><button type="button" className="dash-toolbar-search-icon" aria-label={t('contacts.searchAria')}><IconSearch size={16} /></button><input type="search" placeholder={searchPlaceholder} value={query} onChange={(event) => setQuery(event.target.value)} />{query ? <button type="button" className="dash-toolbar-search-clear" aria-label={t('contacts.clearSearch')} onClick={() => setQuery('')}><IconX size={13} /></button> : null}</div>
            </div>
            <div className="dash-toolbar-controls">
              <StarFilterButton active={starredOnly} onToggle={() => setStarredOnly((only) => !only)} label={t('contacts.starred')} />
              <div className="dash-filter-wrap" ref={filterMenuRef}>
                <button className="dash-filter-trigger" type="button" aria-label={t('contacts.filtersAria')} data-tooltip={t('contacts.filtersAria')} aria-expanded={filterMenuOpen} onClick={() => setFilterMenuOpen((open) => !open)}>
                  <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
                  {sourceFilter !== 'all' || statusFilter !== 'active' || accountFilter !== 'all' ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
                </button>
                {filterMenuOpen ? (
                  <div className="dash-filter-popover" role="dialog" aria-label={t('contacts.filtersDialogAria')}>
                    <div className="dash-filter-popover-head"><strong>{t('contacts.filterTitle')}</strong><button type="button" aria-label={t('contacts.closeFilters')} onClick={() => setFilterMenuOpen(false)}><IconX size={15} /></button></div>
                    <div className="dash-filter-section">
                      <span className="dash-filter-section-title">{t('contacts.initialSource')}</span>
                      <div className="dash-filter-options">
                        {([['all', t('contacts.all')], ['manual', sourceLabel('manual')], ['form', sourceLabel('form')], ['booking', sourceLabel('booking')], ['purchase', sourceLabel('purchase')], ['account', sourceLabel('account')]] as const).map(([value, label]) => <button className={sourceFilter === value ? 'active' : ''} type="button" onClick={() => setSourceFilter(value)} key={value}><span>{label}</span>{sourceFilter === value ? <IconCheck size={14} /> : null}</button>)}
                      </div>
                    </div>
                    <div className="dash-filter-section">
                      <span className="dash-filter-section-title">{t('contacts.status.title')}</span>
                      <div className="dash-filter-options">
                        {([['active', t('contacts.status.activePlural')], ['spam', t('contacts.status.spam')], ['blocked', t('contacts.status.blockedPlural')], ['all', t('contacts.all')]] as const).map(([value, label]) => <button className={statusFilter === value ? 'active' : ''} type="button" onClick={() => setStatusFilter(value)} key={value}><span>{label}</span>{statusFilter === value ? <IconCheck size={14} /> : null}</button>)}
                      </div>
                    </div>
                    <div className="dash-filter-section">
                      <span className="dash-filter-section-title">{t('contacts.account.filterTitle')}</span>
                      <div className="dash-filter-options">
                        {([['all', t('contacts.account.filterAll')], ['yes', t('contacts.account.filterYes')], ['no', t('contacts.account.filterNo')]] as const).map(([value, label]) => <button className={accountFilter === value ? 'active' : ''} type="button" onClick={() => setAccountFilter(value)} key={value}><span>{label}</span>{accountFilter === value ? <IconCheck size={14} /> : null}</button>)}
                      </div>
                    </div>
                    {sourceFilter !== 'all' || statusFilter !== 'active' || accountFilter !== 'all' ? <button className="dash-filter-reset" type="button" onClick={() => { setSourceFilter('all'); setStatusFilter('active'); setAccountFilter('all') }}>{t('contacts.resetFilters')}</button> : null}
                  </div>
                ) : null}
              </div>
              <button type="button" className="dash-filter-trigger" data-tooltip={t('contacts.exportCsv')} aria-label={t('contacts.exportCsvAria')} onClick={exportCsv}><IconDownload size={14} /></button>
              <button type="button" className="dash-primary-btn dash-toolbar-create" onClick={() => { setSaveError(''); setAddOpen(true) }}><IconUserPlus size={14} /><span className="dash-btn-label dash-btn-label--desktop">{t('contacts.add.title')}</span><span className="dash-btn-label dash-btn-label--compact">{t('contacts.add.short')}</span></button>
            </div>
          </header>
        ) : null}
        {contacts.length > 0 ? (
          <ListToolbar
            selection={listSelection}
            actions={[
              { icon: <IconStar size={14} />, label: t('contacts.star'), onClick: () => { toggleStar(listSelection.ids, true); listSelection.clear() } },
              { icon: <IconTrash size={14} />, label: t('contacts.delete'), danger: true, onClick: () => setBulkDeleteOpen(true) },
            ]}
          />
        ) : null}
        {failed ? <div className="dash-placeholder"><span>{t('contacts.loadFailed')}</span></div> : contacts.length === 0 ? (
          <div className="dash-placeholder">
            <IconAddressBook size={34} aria-hidden="true" />
            <strong>{t('contacts.emptyTitle')}</strong>
            <span>{t('contacts.emptyBody')}</span>
            <button type="button" className="dash-primary-btn" onClick={() => { setSaveError(''); setAddOpen(true) }}><IconUserPlus size={14} /> {t('contacts.add.title')}</button>
          </div>
        ) : visible.length === 0 ? (
          <div className="dash-placeholder">
            <IconSearch size={34} aria-hidden="true" />
            <strong>{t('contacts.noResultsTitle')}</strong>
            <span>{t('contacts.noResultsBody')}</span>
            <button type="button" className="dash-secondary-btn" onClick={clearFilters}>{t('contacts.clearFilters')}</button>
          </div>
        ) : (
          <>
          {/* La paginación, encima de la tabla: es donde se decide qué se va a
              mirar, y debajo obligaba a recorrer las diez filas para descubrir
              que había más. */}
          <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('contacts.unit')} />
          <div className="dash-data-table dash-contacts-data-table has-selection has-stars"><div className="dash-data-header" role="row">
            <span className="dash-data-select-cell">
              <SelectCheckbox
                checked={listSelection.allVisible}
                mixed={listSelection.someVisible}
                label={listSelection.allVisible ? t('contacts.unselectAll') : t('contacts.selectAll')}
                onChange={listSelection.toggleAll}
              />
            </span>
            <button className={sort === 'nameAsc' || sort === 'nameDesc' ? 'is-active' : ''} type="button" onClick={() => toggleSort('name')}>{t('contacts.col.name')} <IconChevronDown className={sort === 'nameAsc' ? 'is-asc' : ''} size={13} /></button>
            <button className={sort === 'emailAsc' || sort === 'emailDesc' ? 'is-active' : ''} type="button" onClick={() => toggleSort('email')}>{t('contacts.col.email')} <IconChevronDown className={sort === 'emailAsc' ? 'is-asc' : ''} size={13} /></button>
            <button className={sort === 'lastAsc' || sort === 'lastDesc' ? 'is-active' : ''} type="button" onClick={() => toggleSort('last')}>{t('contacts.col.lastSeen')} <IconChevronDown className={sort === 'lastAsc' ? 'is-asc' : ''} size={13} /></button>
            <button className={sort === 'firstAsc' || sort === 'firstDesc' ? 'is-active' : ''} type="button" onClick={() => toggleSort('first')}>{t('contacts.col.created')} <IconChevronDown className={sort === 'firstAsc' ? 'is-asc' : ''} size={13} /></button>
            <span className="dash-data-header-actions">{t('contacts.actions')}</span>
          </div><div className="dash-project-list">{visible.map((contact: ContactRecord) => (
            <article
              className={`dash-project-row dash-data-row dash-loaded-content-in${listSelection.isSelected(contact.id) ? ' is-selected' : ''}`}
              key={contact.id}
              /* Toda la ficha abre, y el toque se recoge aquí y no sólo en el bloque
                 del nombre: en el teléfono un toque en un hueco de la ficha no abría
                 nada. La casilla, la estrella, el correo y el menú siguen siendo suyos. */
              onClick={openOnRowClick(() => openContact(contact.id))}
            >
              <span className="dash-data-select-cell">
                <SelectCheckbox checked={listSelection.isSelected(contact.id)} label={t('contacts.selectContact', { name: contact.name || contact.email || t('contacts.thisContact') })} onChange={() => listSelection.toggle(contact.id)} />
                <StarButton starred={starred.has(contact.id)} label={t('contacts.starContact', { name: contact.name || contact.email || t('contacts.thisContact') })} onToggle={() => toggleStar([contact.id], !starred.has(contact.id))} />
              </span>
              <div className="dash-project-row-main" role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter') openContact(contact.id) }}>
                {contact.avatarUrl && /^https?:/.test(contact.avatarUrl)
                  ? <span className="dash-project-row-thumb dash-contact-row-thumb has-photo"><img src={contact.avatarUrl} alt="" loading="lazy" /></span>
                  /* Sin foto, el mismo círculo con sus iniciales (como en su ficha), no la tarjeta gris. */
                  : <span className="dash-project-row-thumb dash-contact-row-thumb" aria-hidden="true">{initials(contact) || <IconUser size={18} />}</span>}
                <span className="dash-project-row-copy"><span className="dash-project-row-title">{isNewContact(contact) ? <NewItemDot /> : null}{contact.hasAccount ? <AccountBadge iconOnly label={t('contacts.account.badge')} hint={t('contacts.account.badgeHint')} /> : null}<strong>{contact.name || t('contacts.unnamed')}</strong></span></span>
              </div>
              <div className="dash-list-row-meta dash-contact-row-meta">
                <span className="dash-data-cell dash-contact-email" data-label={t('contacts.col.email')} title={contact.email || undefined}>{contact.email ? <a href={`mailto:${contact.email}`}>{contact.email}</a> : t('contacts.noEmail')}</span>
                {/* La fecha corta de las demás tablas —«15 sept 2026», o «Hoy,
                    08:50»— y no la larga: con dos columnas de fecha seguidas,
                    «16 de septiembre de 2026» dos veces por fila era lo más ancho
                    de la tabla y empujaba el botón de abrir contra el filo. */}
                <time className="dash-data-cell dash-list-date-cell dash-contact-last-cell" data-label={t('contacts.col.lastSeen')} dateTime={contact.lastSeen}><IconPencil className="dash-list-meta-icon" size={14} aria-hidden="true" />{formatDashboardListDate(contact.lastSeen)}</time>
                <time className="dash-data-cell dash-list-date-cell dash-contact-created-cell" data-label={t('contacts.col.created')} dateTime={contact.firstSeen}><IconCalendarEvent className="dash-list-meta-icon" size={14} aria-hidden="true" />{formatDashboardListDate(contact.firstSeen)}</time>
              </div>
              <div className="dash-project-row-actions">
                    <button className="dash-secondary-btn dash-project-row-action" type="button" aria-label={t('contacts.view')} onClick={() => openContact(contact.id)}>
                      <IconEye size={14} aria-hidden="true" />
                      <span className="dash-project-row-action-label">{t('common.open')}</span>
                    </button>
                    {/* Las mismas acciones que la ficha, sin abrirla. Comparten
                        `patchContact` y `setContactToDelete`, así que no hay una
                        segunda forma de bloquear o borrar un contacto. */}
                    <div className={`dash-project-row-menu-wrap${menuOpenId === contact.id ? ' is-open' : ''}${menuOpenId === contact.id && listMenu.up ? ' menu-up' : ''}`}>
                      <button
                        className="project-menu-btn dash-icon-btn dash-page-options-btn"
                        type="button"
                        aria-label={t('contacts.moreOptions')}
                        data-tooltip={t('contacts.options')}
                        aria-expanded={menuOpenId === contact.id}
                        onClick={(event) => {
                          event.stopPropagation()
                          if (menuOpenId !== contact.id) {
                            const rect = event.currentTarget.getBoundingClientRect()
                            const menuWidth = 184
                            /* Seis acciones cuando hay email; cuatro cuando no.
                               Medirlas aquí evita que el menú se pinte primero
                               en una posición y luego salte a la correcta. */
                            const menuHeight = contact.email ? 234 : 168
                            const edge = 12
                            const gap = 8
                            const spaceBelow = window.innerHeight - rect.bottom - edge
                            const spaceAbove = rect.top - edge
                            const up = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
                            const top = up
                              ? Math.max(edge, rect.top - menuHeight - gap)
                              : Math.min(window.innerHeight - menuHeight - edge, rect.bottom + gap)
                            const left = Math.max(edge, Math.min(window.innerWidth - menuWidth - edge, rect.right - menuWidth))
                            setListMenu({ top, left, up, maxHeight: Math.max(160, up ? spaceAbove - gap : spaceBelow - gap) })
                          }
                          setMenuOpenId(menuOpenId === contact.id ? null : contact.id)
                        }}
                      >
                        <IconDots size={16} />
                      </button>
                      {menuOpenId === contact.id ? createPortal(
                        <div
                          className={`project-menu dash-project-row-menu dash-contact-row-menu-portal${listMenu.up ? ' is-up' : ''}`}
                          role="menu"
                          style={{ top: listMenu.top, left: listMenu.left, maxHeight: listMenu.maxHeight, overflowY: 'auto' }}
                        >
                          <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenuOpenId(null); openContact(contact.id) }}><IconEye size={14} /> {t('contacts.view')}</button>
                          {contact.email ? (
                            <>
                              <button
                                className="project-menu-item"
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  setMenuOpenId(null)
                                  void navigator.clipboard.writeText(contact.email).then(
                                    () => showToast(t('contacts.emailCopied')),
                                    () => showToast(t('contacts.emailCopyFailed'), 'error'),
                                  )
                                }}
                              >
                                <IconCopy size={14} /> {t('contacts.copyEmail')}
                              </button>
                              <a className="project-menu-item" role="menuitem" href={`mailto:${contact.email}`} onClick={() => setMenuOpenId(null)}><IconMail size={14} /> {t('contacts.write')}</a>
                            </>
                          ) : null}
                          <div className="project-menu-rule" role="separator" />
                          <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenuOpenId(null); void patchContact(contact, { status: contact.status === 'blocked' ? 'active' : 'blocked' }, 'status') }}>
                            <IconShieldOff size={14} /> {contact.status === 'blocked' ? t('contacts.unblock') : t('contacts.block')}
                          </button>
                          <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenuOpenId(null); void patchContact(contact, { status: contact.status === 'spam' ? 'active' : 'spam' }, 'status') }}>
                            <IconBan size={14} /> {contact.status === 'spam' ? t('contacts.unspam') : t('contacts.markSpam')}
                          </button>
                          <div className="project-menu-rule" role="separator" />
                          <button className="project-menu-item danger" type="button" role="menuitem" onClick={() => { setMenuOpenId(null); setContactToDelete(contact) }}><IconTrash size={14} /> {t('contacts.delete')}</button>
                        </div>,
                        document.body,
                      ) : null}
                    </div>
                  </div>
            </article>
          ))}</div></div></>
        )}
      </div>
    )}
    {editOpen && selected ? createPortal(
      <div className="dash-contact-add-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setEditOpen(false) }}>
        <form
          className="dash-contact-add-modal"
          onSubmit={(event) => {
            event.preventDefault()
            const form = new FormData(event.currentTarget)
            // `FormData.get` puede devolver un File; sólo nos sirve el texto.
            const text = (field: string) => {
              const value = form.get(field)
              return typeof value === 'string' ? value.trim() : ''
            }
            void patchContact(selected, { name: text('name'), phone: text('phone') }, 'edit')
              .then(() => setEditOpen(false))
          }}
        >
          <header>
            <div>
              <span className="dash-contact-event-icon"><IconPencil size={15} /></span>
              <span><h2>{t('contacts.editDialog.title')}</h2><p>{t('contacts.editDialog.subtitle')}</p></span>
            </div>
            <button type="button" aria-label={t('common.close')} onClick={() => setEditOpen(false)}><IconX size={17} /></button>
          </header>
          <div className="dash-contact-add-fields">
            <label>
              <span>{t('contacts.name')}</span>
              <input name="name" autoFocus maxLength={120} defaultValue={selected.name} placeholder={t('contacts.namePlaceholder')} />
            </label>
            <label>
              <span>{t('contacts.phone')}</span>
              <input name="phone" type="tel" maxLength={40} defaultValue={selected.phone} placeholder={t('contacts.phonePlaceholder')} />
            </label>
            {/* El correo identifica a la persona en todo el panel: cambiarlo la
                convertiría en otra distinta y separaría su historial. */}
            <label>
              <span>{t('contacts.email')}</span>
              <input value={selected.email || t('contacts.noEmail')} disabled readOnly />
            </label>
          </div>
          <footer>
            <button type="button" className="dash-secondary-btn" onClick={() => setEditOpen(false)}>{t('common.cancel')}</button>
            <button type="submit" className="dash-primary-btn" disabled={savingField === 'edit'}>
              {savingField === 'edit' ? t('common.saving') : t('contacts.editDialog.save')}
            </button>
          </footer>
        </form>
      </div>,
      document.body,
    ) : null}
    {bulkDeleteOpen ? (
      <ConfirmDialog
        title={listSelection.count === 1 ? t('contacts.bulkDelete.titleOne') : t('contacts.bulkDelete.titleMany', { count: listSelection.count })}
        tone="danger"
        confirmLabel={t('contacts.delete')}
        confirmingLabel={t('contacts.deleting')}
        cancelLabel={t('common.cancel')}
        message={t('contacts.bulkDelete.body')}
        onConfirm={removeSelected}
        onCancel={() => setBulkDeleteOpen(false)}
      />
    ) : null}
    {/* El cobro, encima de la ficha: el mismo diálogo que Pedidos, con el
        recibo de Stripe, la tarjeta, la comisión y las devoluciones. */}
    {orderOpen ? (
      <OrderDetailDialog
        projectId={projectId}
        token={token}
        order={orderOpen}
        /* También aquí, aunque casi siempre sea la persona que ya está debajo:
           un dato que unas veces se puede pulsar y otras no enseña a no
           pulsarlo. Cierra el diálogo y abre esa ficha; si es la misma, se
           queda donde estaba. */
        onOpenContact={(identity) => { setOrderOpen(null); openContact(identity) }}
        {...(onOpenOffer ? { onOpenOffer: (offerId: string) => { setOrderOpen(null); onOpenOffer(offerId) } } : {})}
        onClose={() => setOrderOpen(null)}
      />
    ) : null}
    {bookingOpen ? (
      <BookingDetail
        projectId={projectId}
        token={token}
        booking={bookingOpen}
        {...(() => {
          const summary = selected?.bookings.find((item) => item.id === bookingOpen.id)
          return summary?.imageUrl ? { image: summary.imageUrl } : {}
        })()}
        onClose={() => setBookingOpen(null)}
        onChange={applyContactBooking}
        onOpenContact={(identity) => {
          setBookingOpen(null)
          openContact(identity)
        }}
      />
    ) : null}
    {contactToDelete ? (
      <ConfirmDialog
        title={t('contacts.deleteDialog.title')}
        tone="danger"
        confirmLabel={t('contacts.delete')}
        confirmingLabel={t('contacts.deleting')}
        cancelLabel={t('common.cancel')}
        message={(
          <>
            {t('contacts.deleteDialog.prefix')} <strong>{contactToDelete.name || contactToDelete.email || t('contacts.thisContact')}</strong>
            {contactToDelete.activityCount > 0 ? (
              <> {t('contacts.deleteDialog.withMessages', {
                count: contactToDelete.activityCount,
                messages: contactToDelete.activityCount === 1 ? t('contacts.messageOne') : t('contacts.messageMany'),
              })}</>
            ) : null}
            {t('contacts.deleteDialog.undone')}
          {/* Y al pie: quien acaba de recorrer la página busca el siguiente
              paso donde terminó de leer. Con una sola, sólo la cuenta. */}
          <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('contacts.unit')} alwaysShow />
          </>
        )}
        onConfirm={() => removeContact(contactToDelete)}
        onCancel={() => setContactToDelete(null)}
      />
    ) : null}
    {addOpen ? createPortal(
      <div className="dash-contact-add-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setAddOpen(false) }}>
        <form className="dash-contact-add-modal" onSubmit={(event) => { void saveContact(event) }}>
          <header>
            <div><span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconUserPlus size={15} /></span><span><h2>{t('contacts.add.title')}</h2><p>{t('contacts.add.subtitle')}</p></span></div>
            <button type="button" aria-label={t('common.close')} onClick={() => setAddOpen(false)}><IconX size={17} /></button>
          </header>
          <div className="dash-contact-add-fields">
            <label><span>{t('contacts.name')} <b>*</b></span><input autoFocus required maxLength={120} value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} placeholder={t('contacts.namePlaceholder')} /></label>
            <label><span>{t('contacts.email')} <b>*</b></span><input type="email" required maxLength={254} value={draft.email} onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))} placeholder={t('contacts.emailPlaceholder')} /></label>
            <label><span>{t('contacts.phone')}</span><input type="tel" maxLength={40} value={draft.phone} onChange={(event) => setDraft((current) => ({ ...current, phone: event.target.value }))} placeholder={t('contacts.phonePlaceholder')} /></label>
            <label><span>{t('contacts.add.initialNote')}</span><textarea maxLength={2000} rows={4} value={draft.note} onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))} placeholder={t('contacts.add.initialNotePlaceholder')} /></label>
            {saveError ? <p className="dash-contact-add-error">{saveError}</p> : null}
          </div>
          <footer><button type="button" className="dash-secondary-btn" onClick={() => setAddOpen(false)}>{t('common.cancel')}</button><button type="submit" className="dash-primary-btn" disabled={saving || !draft.name.trim() || !draft.email.trim()}>{saving ? t('common.saving') : t('contacts.add.title')}</button></footer>
        </form>
      </div>,
      document.body,
    ) : null}
    </>
  )
}

/** El menú ⋯ de la cabecera de una ficha: spam, bloquear y, separado, eliminar. */
function ContactMoreMenu({ labels, busy, onSpam, onBlock, onDelete }: {
  labels: { more: string; spam: string; block: string; remove: string }
  busy: boolean
  onSpam: () => void
  onBlock: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (event: PointerEvent) => { if (!rootRef.current?.contains(event.target as Node)) setOpen(false) }
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', escape) }
  }, [open])
  const run = (action: () => void) => () => { setOpen(false); action() }
  return (
    <div className="dash-cp-more" ref={rootRef}>
      <button type="button" className="dash-secondary-btn dash-cp-more-btn" aria-label={labels.more} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <IconSettings size={16} />
      </button>
      {open ? (
        <div className="dash-cp-more-menu" role="menu">
          <button type="button" role="menuitem" disabled={busy} onClick={run(onSpam)}><IconBan size={14} /> {labels.spam}</button>
          <button type="button" role="menuitem" disabled={busy} onClick={run(onBlock)}><IconShieldOff size={14} /> {labels.block}</button>
          <hr />
          <button type="button" role="menuitem" className="is-danger" onClick={run(onDelete)}><IconTrash size={14} /> {labels.remove}</button>
        </div>
      ) : null}
    </div>
  )
}
